<template>
  <section class="mig-page">
    <top-menu/>
    <page-header headerName="Migração IUGU → ASAAS (MAM)" :showBtn="false" btnName="" btnRoute="" css="container-fluid"/>

    <div class="page-content container-fluid">

      <div v-if="!password" class="gate">
        <div class="gate-card">
          <h3>Área restrita</h3>
          <p>Esta tela cria clientes e assinaturas no ASAAS e suspende assinaturas na IUGU. Informe a senha da migração para continuar.</p>
          <input type="password" v-model="passwordInput" placeholder="Senha da migração" @keyup.enter="unlock" autofocus/>
          <p v-if="authError" class="txt-danger">{{ authError }}</p>
          <button class="btn-primary" :disabled="!passwordInput || authChecking" @click="unlock">
            {{ authChecking ? 'Validando...' : 'Entrar' }}
          </button>
        </div>
      </div>

      <template v-else>

        <div class="seg-tabs">
          <a class="seg-tab" :class="{ on: segment === 'MENSAL' }" @click="setSegment('MENSAL')">Mensais <b>{{ segmentCounts.MENSAL }}</b><small>núcleo da migração</small></a>
          <a class="seg-tab" :class="{ on: segment === 'NAO_MENSAL' }" @click="setSegment('NAO_MENSAL')">Não mensais <b>{{ segmentCounts.NAO_MENSAL }}</b><small>bimestral, trimestral, semestral, anual, bianual</small></a>
        </div>
        <div class="seg-note" v-if="segment === 'NAO_MENSAL'">
          <strong>Não mensais:</strong> o plano do Master (valor e periodicidade) não muda. O que passa para o ASAAS é a recorrência que a IUGU cobra hoje, no ciclo dela (ex.: TotalPass mensal de um cliente anual, ou a anuidade).
          Simular mostra o ciclo e o valor de cada uma; migrar pede <strong>MIGRAR NAO MENSAL</strong>. Nada daqui entra nas contas nem nas seleções da aba Mensais.
          <span v-if="!segmentCounts.NAO_MENSAL" class="txt-warn">Nenhuma ainda: rode o script 06 em todos os bancos e clique em Atualizar base.</span>
        </div>

        <div class="hero">
          <div class="hero-main">
            <div class="hero-title">
              <span class="big">{{ stats.migrated }}</span>
              <span>de {{ stats.scope }} assinaturas migradas</span>
              <span class="pct">{{ pct(stats.migrated, stats.scope) }}%</span>
            </div>
            <div class="segbar" :title="segTitle">
              <div class="seg seg-ok" :style="{ width: share(stats.migrated) }"></div>
              <div class="seg seg-run" :style="{ width: share(stats.inProgress) }"></div>
              <div class="seg seg-err" :style="{ width: share(stats.errors) }"></div>
            </div>
            <div class="legend">
              <span class="lg lg-ok" @click="quickStatus('MIGRADA')">Migradas {{ stats.migrated }}</span>
              <span class="lg lg-run" @click="quickStatus('NA_FILA')">Na fila / processando {{ stats.inProgress }}</span>
              <span class="lg lg-err" @click="quickStatus('ERRO')">Com erro {{ stats.errors }}</span>
              <span class="lg lg-pend" @click="quickStatus('PENDENTE')">A migrar {{ stats.pending }}</span>
              <span class="lg lg-exc" @click="quickStatus('EXCLUIDA')">Excluídas {{ stats.excluded }} (fora da conta)</span>
            </div>
          </div>

          <div class="hero-side">
            <div class="kpi">
              <span class="kpi-label">MRR já migrado (valor ASAAS)</span>
              <span class="kpi-value">{{ money(stats.mrrMigrated) }}</span>
            </div>
            <div class="kpi">
              <span class="kpi-label">MRR a migrar (valor IUGU)</span>
              <span class="kpi-value">{{ money(stats.mrrToMigrate) }}</span>
              <span class="kpi-sub" :class="{ 'txt-warn': stats.toMigrateUnsynced }">
                {{ stats.toMigrateSynced }} com dado IUGU<template v-if="stats.toMigrateUnsynced"> · {{ stats.toMigrateUnsynced }} sem dado IUGU (fora da soma)</template>
              </span>
            </div>
            <div class="kpi" :class="{ 'kpi-alert': stats.critical }" @click="stats.critical && quickCritical()">
              <span class="kpi-label">Incompletas com ASAAS criado</span>
              <span class="kpi-value">{{ stats.critical }}</span>
              <span class="kpi-sub">risco de cobrança dupla</span>
            </div>
          </div>
        </div>

        <div class="steps">
          <div class="step" :class="stepClass(1)">
            <span class="n">1</span>
            <div>
              <strong>Atualizar a base</strong>
              <small>Lê os 7 bancos: MAM, mensal, ativa, só IUGU. <template v-if="lastCohortAt">Última: {{ fmtDateTime(lastCohortAt) }}</template></small>
            </div>
            <button class="btn-secondary" :disabled="refreshing || runner.active" @click="refreshCohort">{{ refreshing ? 'Lendo bancos...' : 'Atualizar base' }}</button>
          </div>
          <div class="step" :class="stepClass(2)">
            <span class="n">2</span>
            <div>
              <strong>Ler a IUGU</strong>
              <small>{{ stats.syncOk }} lidas · {{ stats.syncPending }} pendentes · {{ stats.syncError }} com erro</small>
            </div>
            <button class="btn-secondary" :disabled="runner.active || !syncCandidates.length" @click="startSync(syncCandidates)">Ler pendentes ({{ syncCandidates.length }})</button>
          </div>
          <div class="step" :class="stepClass(3)">
            <span class="n">3</span>
            <div>
              <strong>Escolher o período</strong>
              <small>Filtre pelo vencimento IUGU. A IUGU gera o boleto ~7 dias antes (alguns planos antes disso).</small>
            </div>
          </div>
          <div class="step" :class="stepClass(4)">
            <span class="n">4</span>
            <div>
              <strong>Simular e migrar</strong>
              <small>Selecione, simule e envie para a fila. Dá para pausar e retomar.</small>
            </div>
            <button v-if="(stats.inProgress || serverCounts.paused) && !server.visible" class="btn-primary" @click="openServerQueue">Acompanhar fila ({{ stats.inProgress + serverCounts.paused }})</button>
          </div>
          <div class="step" :class="stepClass(5)">
            <span class="n">5</span>
            <div>
              <strong>Tratar erros</strong>
              <small>Corrija o motivo e reprocesse, ou exclua da migração.</small>
            </div>
          </div>
        </div>

        <div v-if="shardsFailed.length" class="banner banner-err">
          Bancos que não responderam agora: {{ shardsFailed.join(' · ') }}. Os números abaixo estão INCOMPLETOS até eles voltarem — clique em Recarregar.
        </div>

        <div v-if="cardsError" class="banner banner-err">{{ cardsError }}</div>
        <div v-for="(w, i) in itemWarnings" :key="'w' + i" class="banner banner-err">{{ w }}</div>

        <div v-if="refreshResult && refreshResult.shardsFailed && refreshResult.shardsFailed.length" class="banner banner-err">
          Bancos que não responderam na última atualização (os itens deles NÃO foram alterados): {{ refreshResult.shardsFailed.join(' · ') }}
        </div>

        <div v-if="server.visible" class="runner server-queue" :class="{ paused: !serverWorker.running && serverCounts.queued + serverCounts.processing > 0 }">
          <div class="runner-head">
            <strong>Fila no servidor</strong>
            <span class="tag" :class="serverWorker.running ? 'tag-info' : 'tag-muted'">{{ serverStateLabel }}</span>
            <span>na fila {{ serverCounts.dueNow }}</span>
            <span>processando {{ serverCounts.processing }}</span>
            <span class="warn" v-if="serverCounts.waitingRetry">aguardando nova tentativa {{ serverCounts.waitingRetry }}</span>
            <span class="muted" v-if="serverCounts.paused">pausadas {{ serverCounts.paused }}</span>
            <span class="warn" v-if="serverCounts.awaitingCard">aguardando cartão {{ serverCounts.awaitingCard }}</span>
            <span class="ok">migradas {{ serverWorker.ok || 0 }}</span>
            <span class="err">erro {{ serverWorker.failed || 0 }}</span>
            <span class="muted" v-if="serverWorker.startedAt">desde {{ fmtTime(serverWorker.startedAt) }} · {{ serverWorker.startedBy }}</span>
            <div class="runner-actions">
              <button v-if="serverCounts.dueNow || serverCounts.waitingRetry" class="btn-secondary" :disabled="server.busy" @click="pauseServerQueue" title="Tira da fila o que ainda não começou. O que já está em andamento termina normalmente.">Pausar fila</button>
              <button v-if="serverCounts.paused" class="btn-primary" :disabled="server.busy" @click="resumeServerQueue">Retomar fila ({{ serverCounts.paused }})</button>
              <button v-if="!serverBusyWork" class="btn-secondary" @click="server.visible = false">Fechar</button>
            </div>
          </div>
          <div class="bar"><div class="bar-fill" :style="{ width: serverProgress + '%' }"></div></div>
          <p class="runner-msg">A fila roda no servidor: pode fechar esta aba, e qualquer aba aberta nesta tela acompanha.<template v-if="serverWorker.lastBeatAt"> Última atividade às {{ fmtTime(serverWorker.lastBeatAt) }}.</template><template v-if="server.checkedAt"> Atualizado às {{ server.checkedAt }}.</template></p>
          <p class="runner-msg txt-danger" v-if="server.error">{{ server.error }}</p>
          <p class="runner-msg txt-danger" v-if="serverWorker.lastError">Último aviso do servidor ({{ fmtTime(serverWorker.lastErrorAt) }}): {{ serverWorker.lastError }} <a class="laudo-link" @click="openWorkerLaudo">ver laudo</a></p>
          <ul class="runner-log">
            <li v-for="e in server.events" :key="e.contextCode + '-' + e.id" :class="serverEventClass(e)">
              <span class="t">{{ fmtTime(e.createdAt) }}</span> <a @click="openDrawerById(e.subscriptionId)">{{ e.companyName || e.subscriptionId }}</a> · {{ eventLabel(e.type) }}: {{ e.message }}
            </li>
            <li v-if="!server.events.length" class="log-info">Sem eventos recentes da fila.</li>
          </ul>
        </div>

        <div v-if="runner.active || runner.finished" class="runner" :class="{ paused: runner.paused }">
          <div class="runner-head">
            <strong>Lendo a IUGU</strong>
            <span>{{ runner.done }} / {{ runner.total }}</span>
            <span class="ok">ok {{ runner.ok }}</span>
            <span class="err">erro {{ runner.failed }}</span>
            <span class="warn" v-if="runner.retrying">aguardando nova tentativa {{ runner.retrying }}</span>
            <span class="muted" v-if="runner.skipped">puladas {{ runner.skipped }}</span>
            <span class="muted" v-if="runner.throttleMs">ritmo IUGU 1 chamada/{{ runner.throttleMs }}ms</span>
            <div class="runner-actions">
              <button v-if="runner.active && !runner.paused" class="btn-secondary" @click="pauseRunner">Pausar</button>
              <button v-if="runner.active && runner.paused" class="btn-primary" @click="resumeRunner">Continuar</button>
              <button v-if="runner.active" class="btn-secondary" @click="stopRunner" title="Encerra o acompanhamento. O que está na fila continua lá e pode ser retomado depois.">Parar</button>
              <button v-if="!runner.active" class="btn-secondary" @click="closeRunner">Fechar</button>
            </div>
          </div>
          <div class="bar"><div class="bar-fill" :style="{ width: pct(runner.done, runner.total) + '%' }"></div></div>
          <p class="runner-msg" v-if="runner.message">{{ runner.message }}</p>
          <ul class="runner-log">
            <li v-for="(l, i) in runner.log" :key="i" :class="'log-' + l.kind">
              <span class="t">{{ l.time }}</span> {{ l.text }} <a v-if="l.incident" class="laudo-link" @click="openIncident(l.incident)">ver laudo</a>
            </li>
          </ul>
        </div>

        <div class="relink-banner" v-if="staleLinkIds.length">
          <div>
            <strong>{{ staleLinkIds.length }} assinatura(s) com a assinatura IUGU errada no Master</strong>
            <small>O Master recebe boletos IUGU recentes delas, mas a assinatura IUGU vinculada parou (suspensa, sem ciclo ou sem boleto há meses). Migrar assim suspende a assinatura errada e o cliente segue cobrado na IUGU.</small>
          </div>
          <div class="relink-actions">
            <button class="btn-secondary" @click="showStaleLinks">Ver na lista</button>
            <button class="btn-primary" @click="openRelink(staleLinkIds)">Corrigir vínculo ({{ staleLinkIds.length }})</button>
          </div>
        </div>

        <div class="errors-box" v-if="errorGroups.length">
          <div class="errors-head">
            <strong>Erros por motivo</strong>
            <small>clique para filtrar · {{ stats.errors }} assinatura(s)</small>
          </div>
          <div class="error-chips">
            <div v-for="g in errorGroups" :key="g.code" class="error-chip" :class="{ critical: g.critical, active: filters.errorCode === g.code }" @click="toggleErrorFilter(g.code)">
              <div class="ec-top"><span class="ec-code">{{ errorLabel(g.code) }}</span><span class="ec-count">{{ g.count }}</span></div>
              <div class="ec-help">{{ errorHelp(g.code) }}</div>
              <button v-if="CARD_CODES.indexOf(g.code) !== -1" class="btn-secondary ec-action" @click.stop="openCardLinks(idsWithError(g.code))">Gerar link de cartão ({{ g.count }})</button>
              <button v-if="FORCE_CODES.indexOf(g.code) !== -1" class="btn-secondary ec-action" @click.stop="openMigrate(idsWithError(g.code), true)">Migrar mesmo assim ({{ g.count }})</button>
              <button v-if="RELINK_CODES.indexOf(g.code) !== -1" class="btn-secondary ec-action" @click.stop="openRelink(idsWithError(g.code))">Corrigir vínculo IUGU ({{ g.count }})</button>
            </div>
          </div>
        </div>

        <div class="filters">
          <div class="f">
            <label>Buscar</label>
            <input type="text" v-model="filters.search" placeholder="Empresa, CNPJ, e-mail ou id"/>
          </div>
          <div class="f">
            <label>Status</label>
            <select v-model="filters.status">
              <option value="ATIVOS">Em andamento (sem excluídas)</option>
              <option value="PENDENTE">A migrar</option>
              <option value="NA_FILA">Na fila / processando</option>
              <option value="AGUARDANDO_CARTAO">Aguardando cartão</option>
              <option value="MIGRADA">Migradas</option>
              <option value="ERRO">Com erro</option>
              <option value="EXCLUIDA">Excluídas</option>
              <option value="REVERTIDA">Revertidas</option>
              <option value="FORA">Saíram do critério</option>
              <option value="TODOS">Todos</option>
            </select>
          </div>
          <div class="f">
            <label>Vencimento IUGU de</label>
            <input type="date" v-model="filters.dueFrom"/>
          </div>
          <div class="f">
            <label>até</label>
            <input type="date" v-model="filters.dueTo"/>
          </div>
          <div class="f presets">
            <label>Atalhos de período</label>
            <div class="chips">
              <span class="chip" @click="presetDays(0, 7)" title="Risco: a IUGU pode já ter gerado o boleto">Próx. 7 dias</span>
              <span class="chip" @click="presetDays(8, 30)">8 a 30 dias</span>
              <span class="chip" @click="presetToMonthEnd(14)">D+14 até o fim do mês</span>
              <span class="chip" @click="presetNextMonth">Próximo mês</span>
              <span class="chip" @click="filters.dueFrom = ''; filters.dueTo = ''">Sem período</span>
              <span class="chip" @click="filters.payMonth = currentMonthKey" title="Quem já tem a fatura deste mês paga na IUGU">Fatura do mês paga</span>
            </div>
          </div>
          <div class="f">
            <label>Fatura IUGU paga em</label>
            <select v-model="filters.payMonth">
              <option value="">Tanto faz</option>
              <option v-for="mo in monthOptions" :key="mo.key" :value="mo.key">{{ mo.label }}</option>
            </select>
          </div>
          <div class="f">
            <label>Leva de comunicação</label>
            <select v-model="filters.wave">
              <option value="">Todas</option>
              <option value="__SEM__">Sem leva</option>
              <option v-for="w in waves" :key="w" :value="w">{{ w }}</option>
            </select>
          </div>
          <div class="f">
            <label>Pagamento na IUGU</label>
            <select v-model="filters.payKind">
              <option value="">Tanto faz</option>
              <option value="CARTAO">Cartão</option>
              <option value="BOLETO">Boleto ou pix</option>
              <option value="INDEFINIDO">Não identificado</option>
              <option value="DIVERGE">Diferente do Master</option>
            </select>
          </div>
          <div class="f">
            <label>Boleto IUGU do ciclo</label>
            <select v-model="filters.cycleInvoiced">
              <option value="">Tanto faz</option>
              <option value="nao">Ainda não gerado</option>
              <option value="sim">Já gerado</option>
            </select>
          </div>
          <div class="f checks">
            <label><input type="checkbox" v-model="filters.mrrDivergent"/> MRR IUGU ≠ Master</label>
            <label><input type="checkbox" v-model="filters.cappedDay"/> Vence após dia 27</label>
            <label><input type="checkbox" v-model="filters.noIugu"/> Sem dado IUGU</label>
            <label title="O Master recebe boleto IUGU recente, mas a assinatura IUGU vinculada parou"><input type="checkbox" v-model="filters.staleLink"/> Assinatura IUGU errada</label>
          </div>
          <div class="f actions">
            <button class="btn-secondary" @click="clearFilters">Limpar filtros</button>
            <button class="btn-secondary" @click="load" :disabled="loading">Recarregar</button>
          </div>
        </div>

        <div class="window-info" v-if="filters.dueFrom || filters.dueTo">
          Período {{ filters.dueFrom ? fmtDate(filters.dueFrom) : '...' }} a {{ filters.dueTo ? fmtDate(filters.dueTo) : '...' }}:
          <strong>{{ windowStats.total }}</strong> assinatura(s), MRR IUGU {{ money(windowStats.mrr) }}
          <span v-if="windowStats.invoiced" class="txt-warn"> · {{ windowStats.invoiced }} já com boleto IUGU do ciclo (a 1ª cobrança ASAAS irá para o mês seguinte)</span>
          <span v-if="windowStats.unsynced" class="txt-warn"> · {{ windowStats.unsynced }} sem dado IUGU (não entram no filtro de período até serem lidas)</span>
        </div>

        <div class="toolbar">
          <div class="count">
            {{ filteredRows.length }} assinatura(s) no filtro
            <template v-if="selectedCount"> · <strong>{{ selectedCount }} selecionada(s)</strong> · MRR {{ money(selectedMrr) }}</template>
          </div>
          <div class="toolbar-right">
            <button v-if="laudos.length" class="btn-secondary" @click="openIncidentList">Laudos técnicos ({{ laudos.length }})</button>
            <button class="btn-secondary" :disabled="!stats.migrated" @click="openReconcile(migratedIds)" title="Confere as migradas e corrige o que der: vincula os boletos do ASAAS no Master, aponta gateway/assinatura, alinha o MRR e avisa o que precisa de ação">Reconciliar migradas ({{ stats.migrated }})</button>
            <button v-if="activeCardLinks.length" class="btn-secondary" @click="exportCardLinks(null)">Links de cartão ({{ activeCardLinks.length }})</button>
            <button class="btn-secondary" :disabled="!stats.migrated" @click="exportCommsReport" title="Uma linha por assinatura migrada: gateway, vencimento antes/depois, valor, 1ª cobrança e texto sugerido para avisar o cliente">Relatório de comunicação</button>
            <button class="btn-secondary" @click="openPaste">Colar lista</button>
            <button class="btn-secondary" @click="openCsvImport">Importar CSV</button>
            <input ref="csvInput" type="file" accept=".csv,.txt" style="display:none" @change="onCsvFile"/>
            <button class="btn-primary" :disabled="!filteredRows.length" @click="openExport">Exportar planilha</button>
          </div>
        </div>

        <div class="select-banner" v-if="pageAllSelected && filteredRows.length > pagedRows.length && filteredSelectedCount < filteredRows.length">
          As {{ pagedRows.length }} desta página estão selecionadas.
          <a @click="selectAllFiltered">Selecionar todas as {{ filteredRows.length }} do filtro</a>
        </div>
        <div class="select-banner" v-else-if="filteredSelectedCount && filteredSelectedCount === filteredRows.length && filteredRows.length > pagedRows.length">
          Todas as {{ filteredRows.length }} do filtro estão selecionadas. <a @click="clearSelection">Limpar seleção</a>
        </div>

        <div class="table-wrap" v-if="!loading">
          <table class="grid">
            <thead>
              <tr>
                <th class="sticky-col"><input type="checkbox" :checked="pageAllSelected" @change="togglePage"/></th>
                <th @click="sortBy('companyName')" class="sortable">Empresa {{ sortMark('companyName') }}</th>
                <th @click="sortBy('status')" class="sortable">Status {{ sortMark('status') }}</th>
                <th @click="sortBy('iuguPaymentKind')" class="sortable">Pagamento IUGU {{ sortMark('iuguPaymentKind') }}</th>
                <th @click="sortBy('iuguExpiresAt')" class="sortable">Venc. IUGU {{ sortMark('iuguExpiresAt') }}</th>
                <th class="num sortable" @click="sortBy('mrrMaster')">{{ segment === 'NAO_MENSAL' ? 'Plano no Master' : 'MRR assinatura' }} {{ sortMark('mrrMaster') }}</th>
                <th class="num sortable" @click="sortBy('iuguMrr')">{{ segment === 'NAO_MENSAL' ? 'Recorrência IUGU' : 'MRR IUGU' }} {{ sortMark('iuguMrr') }}</th>
                <th @click="sortBy('iuguLastInvoiceDueDate')" class="sortable">Último boleto IUGU {{ sortMark('iuguLastInvoiceDueDate') }}</th>
                <th>ASAAS</th>
                <th>Telefone</th>
                <th>E-mail</th>
                <th>E-mail financeiro</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(r, idx) in pagedRows" :key="r.subscriptionId"
                  :class="{ 'row-selected': isSelected(r), 'row-critical': isCritical(r) }">
                <td class="sticky-col"><input type="checkbox" :checked="isSelected(r)" @click="toggleRow(r, idx, $event)"/></td>
                <td class="company">
                  <a class="name" title="1 clique: painel da migração · 2 cliques: abre os detalhes da empresa no Master" @click="onNameClick(r)" @dblclick.prevent="onNameDblClick(r)">{{ r.companyName }}</a>
                  <a class="ext" :href="r.masterUrl" target="_blank" title="Abrir no Master">↗</a>
                  <div class="sub">{{ r.cnpj }} · {{ r.contextCode }}<span v-if="!r.companyIsActive" class="tag tag-muted">inativa</span><span v-if="r.paymentMethod === 2" class="tag tag-warn">cartão</span><span v-if="r.commsWave" class="tag tag-wave" :title="'Leva de comunicação definida por ' + (r.commsWaveBy || '') + ' em ' + fmtDateTime(r.commsWaveAt)">{{ r.commsWave }}</span></div>
                </td>
                <td class="status">
                  <span class="badge" :class="'b-' + statusKey(r)">{{ statusLabel(r) }}</span>
                  <div class="sub err" v-if="r.status === 'ERRO'" :title="r.errorMessage">{{ errorLabel(r.errorCode) }}</div>
                  <div class="sub" v-if="isStaleLink(r) && !(r.status === 'ERRO' && RELINK_CODES.indexOf(r.errorCode) !== -1)"><span class="tag tag-warn" title="O Master recebe boleto IUGU recente, mas a assinatura IUGU vinculada parou: use Corrigir vínculo IUGU">assinatura IUGU errada</span></div>
                  <div class="sub warn" v-else-if="r.status === 'NA_FILA' && r.errorCode === 'RETENTATIVA'" :title="r.errorMessage">nova tentativa {{ fmtTime(r.nextAttemptAt) }}</div>
                  <div class="sub muted" v-else-if="r.status === 'EXCLUIDA'" :title="r.excludedReason">{{ r.excludedReason || 'sem motivo' }}</div>
                  <div class="sub" v-if="cardOf(r)" :class="cardClass(cardOf(r))">
                    {{ cardSummary(cardOf(r)) }}
                    <a v-if="cardOf(r).state === 'ATIVO' && cardOf(r).link" class="laudo-link" @click.stop="copyText(cardOf(r).link, 'Link copiado')">copiar link</a>
                  </div>
                </td>
                <td :title="payTitle(r)">
                  <span v-if="payKnown(r)" class="tag" :class="payTagClass(r.iuguPaymentKind)">{{ payLabel(r.iuguPaymentKind) }}</span>
                  <span v-else class="muted">{{ r.iuguSyncStatus === 'OK' ? 'não identificado' : '—' }}</span>
                  <div class="sub" v-if="payDiverges(r)"><span class="tag tag-warn">Master: {{ r.paymentMethod === 2 ? 'cartão' : 'boleto' }}</span></div>
                </td>
                <td>
                  <template v-if="r.iuguExpiresAt">{{ fmtDate(r.iuguExpiresAt) }}</template>
                  <span v-else class="muted" :title="r.iuguSyncError || ''">{{ r.iuguSyncStatus === 'ERRO' ? 'erro IUGU' : 'não lido' }}</span>
                  <div class="sub">
                    <span v-if="dayOf(r.iuguExpiresAt) > 27" class="tag tag-info" title="No ASAAS o vencimento fica no dia 27">dia {{ dayOf(r.iuguExpiresAt) }}→27</span>
                    <span v-if="r.iuguCycleInvoiced" class="tag tag-warn" title="A IUGU já gerou o boleto deste ciclo; a 1ª cobrança ASAAS vai para o mês seguinte">boleto do ciclo gerado</span>
                  </div>
                </td>
                <td class="num">{{ money(r.mrrMaster) }}<div class="sub muted" v-if="isNonMonthly(r)">{{ planTypeLabel(r.planType) }} · não muda</div></td>
                <td class="num">
                  <template v-if="r.iuguMrr !== null && r.iuguMrr !== undefined">
                    <span :class="{ 'txt-warn': hasDivergence(r) }" :title="hasDivergence(r) ? 'Diferente do Master: na migração o Master assume o valor da IUGU' : ''">{{ money(r.iuguMrr) }}</span>
                    <div class="sub" v-if="r.iuguCycleMonths && (isNonMonthly(r) || r.iuguCycleMonths !== 1)"><span class="tag tag-info">{{ cycleLabel(r.iuguCycleMonths) }}</span></div>
                  </template>
                  <span v-else class="muted">—</span>
                </td>
                <td>
                  <template v-if="r.iuguLastInvoiceDueDate">
                    <a v-if="r.iuguLastInvoiceUrl" :href="r.iuguLastInvoiceUrl" target="_blank" class="inv-date" :class="'inv-' + invoiceTone(r.iuguLastInvoiceStatus)">{{ fmtDate(r.iuguLastInvoiceDueDate) }}</a>
                    <span v-else class="inv-date" :class="'inv-' + invoiceTone(r.iuguLastInvoiceStatus)">{{ fmtDate(r.iuguLastInvoiceDueDate) }}</span>
                    <div class="sub"><span class="inv-pill" :class="'inv-' + invoiceTone(r.iuguLastInvoiceStatus)">{{ invoiceStatusLabel(r.iuguLastInvoiceStatus) }}</span> · {{ r.iuguLastInvoiceTotal }}</div>
                    <div class="sub ok-text" v-if="r.iuguPaidDueDate && dateKey(r.iuguPaidDueDate) !== dateKey(r.iuguLastInvoiceDueDate)">última paga: venc. {{ fmtDate(r.iuguPaidDueDate) }}</div>
                  </template>
                  <span v-else class="muted">—</span>
                </td>
                <td class="ids">
                  <template v-if="r.asaasSubscriptionId">
                    <a :href="asaasSubLink(r)" target="_blank">{{ money(r.asaasValue) }}</a>
                    <div class="sub">1º venc. {{ fmtDate(r.asaasNextDueDate) }}</div>
                  </template>
                  <span v-else class="muted">—</span>
                </td>
                <td>{{ r.phone || '—' }}</td>
                <td class="mail">{{ r.email || '—' }}</td>
                <td class="mail">{{ r.financialEmail || '—' }}</td>
              </tr>
            </tbody>
          </table>
          <p v-if="!filteredRows.length" class="empty">
            <template v-if="!items.length">A base está vazia. Comece pelo passo 1: <a @click="refreshCohort">Atualizar base</a>.</template>
            <template v-else>Nenhuma assinatura para os filtros atuais.</template>
          </p>
        </div>
        <div v-else class="empty">Carregando...</div>

        <div class="pager" v-if="filteredRows.length > pageSize">
          <button class="btn-secondary" :disabled="page === 1" @click="page--">‹</button>
          <span>Página {{ page }} de {{ pageCount }}</span>
          <button class="btn-secondary" :disabled="page >= pageCount" @click="page++">›</button>
          <select v-model.number="pageSize">
            <option :value="50">50 por página</option>
            <option :value="100">100 por página</option>
            <option :value="250">250 por página</option>
          </select>
        </div>

        <div class="hist-box card-box" v-if="cardRowsAll.length">
          <div class="hist-head">
            <h4>Links de cartão</h4>
            <span class="muted">{{ cardRowsAll.length }} link(s) · o link vale 24h</span>
            <div class="hist-actions">
              <select v-model="cardPanel.batch">
                <option value="">Todos os lotes</option>
                <option v-for="b in cardBatches" :key="b" :value="b">{{ b }}</option>
              </select>
              <input type="text" v-model="cardPanel.search" placeholder="Empresa ou CNPJ"/>
              <button class="btn-secondary" @click="loadCards">Atualizar</button>
            </div>
          </div>
          <div class="hist-chips">
            <span v-for="s in cardSegments" :key="s.key" class="chip" :class="{ on: cardPanel.segment === s.key, warn: s.key === 'VENCENDO' && s.count }" @click="cardPanel.segment = s.key; cardPanel.selected = {}">
              {{ s.label }} <b>{{ s.count }}</b>
            </span>
          </div>
          <div class="card-bulk" v-if="cardSelectedIds.length">
            <strong>{{ cardSelectedIds.length }} selecionado(s)</strong>
            <button class="btn-primary" :disabled="cardPanel.busy" @click="markCardsSent(cardSelectedIds, true)">Marcar como enviados</button>
            <button class="btn-secondary" :disabled="cardPanel.busy" @click="markCardsSent(cardSelectedIds, false)">Desmarcar envio</button>
            <button class="btn-secondary" @click="openCardLinks(cardSelectedIds)">Gerar novo link</button>
            <button class="btn-secondary" @click="exportCardLinks(cardSelectedIds)">Exportar</button>
            <button class="btn-link" @click="cardPanel.selected = {}">Limpar</button>
          </div>
          <table class="hist-table" v-if="cardRows.length">
            <thead>
              <tr>
                <th><input type="checkbox" :checked="cardAllSelected" @change="toggleCardAll"/></th>
                <th>Empresa</th><th>Lote</th><th>Etapa</th><th>Validade</th><th>Envio</th><th>Valor / 1ª cobrança</th><th>Contato</th><th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="x in cardRows" :key="x.card.subscriptionId">
                <td><input type="checkbox" :checked="!!cardPanel.selected[x.card.subscriptionId]" @change="toggleCard(x.card.subscriptionId)"/></td>
                <td><a @click="openDrawerById(x.card.subscriptionId)">{{ x.item.companyName || x.card.subscriptionId }}</a><div class="sub muted">{{ x.item.cnpj }}</div></td>
                <td class="nowrap">{{ x.card.batchName || '—' }}</td>
                <td class="nowrap"><span :class="cardClass(x.card)">{{ cardStageLabel(x.card) }}</span><div v-if="x.card.lastError" class="sub txt-danger">{{ x.card.lastError }}</div></td>
                <td class="nowrap">{{ cardTimeLeft(x.card) }}</td>
                <td class="nowrap">{{ x.card.sentAt ? fmtDateTime(x.card.sentAt) + ' · ' + (x.card.sentBy || '') : '—' }}</td>
                <td class="nowrap">{{ x.card.value ? money(x.card.value) : '—' }}<div class="sub muted" v-if="x.card.nextDueDate">1ª {{ fmtDate(x.card.nextDueDate) }}</div><div class="sub muted" v-else-if="x.card.firstChargeMode === 'DATE'">1ª escolhida {{ fmtDate(x.card.firstChargeDate) }}</div><div class="sub muted" v-else-if="x.card.firstChargeMode === 'NEXT_CYCLE'">1ª no ciclo seguinte</div></td>
                <td><div>{{ x.item.financialEmail || x.item.email || '—' }}</div><div class="sub muted">{{ x.item.phone }}</div></td>
                <td class="nowrap">
                  <template v-if="x.card.state === 'ATIVO'">
                    <a class="laudo-link" @click="copyText(x.card.link, 'Link copiado')">copiar link</a>
                    <a class="laudo-link" @click="copyText(cardMessage(x.item), 'Mensagem copiada')">copiar mensagem</a>
                    <a v-if="!x.card.sentAt" class="laudo-link" @click="markCardsSent([x.card.subscriptionId], true)">marcar enviado</a>
                    <a v-else class="laudo-link" @click="markCardsSent([x.card.subscriptionId], false)">desmarcar</a>
                  </template>
                  <a v-if="x.card.state === 'EXPIRADO' || x.card.state === 'FALHOU'" class="laudo-link" @click="openCardLinks([x.card.subscriptionId])">gerar novo link</a>
                </td>
              </tr>
            </tbody>
          </table>
          <p v-else class="empty">Nenhum link nesta etapa.</p>
        </div>

        <div class="hist-box">
          <div class="hist-head">
            <h4>Histórico da migração</h4>
            <span class="muted">{{ historyRows.length }} evento(s)<template v-if="history.loadedAt"> · atualizado às {{ history.loadedAt }}</template></span>
            <div class="hist-actions">
              <input type="text" v-model="history.search" placeholder="Filtrar por empresa ou texto"/>
              <button class="btn-secondary" :disabled="history.loading" @click="loadHistory">{{ history.loading ? 'Carregando...' : (history.loaded ? 'Atualizar' : 'Carregar histórico') }}</button>
              <button class="btn-secondary" :disabled="!historyRows.length" @click="exportHistory">Exportar histórico</button>
            </div>
          </div>
          <div class="hist-chips">
            <span v-for="g in historyGroups" :key="g.key" class="chip" :class="{ on: history.group === g.key }" @click="history.group = g.key; history.limit = 200">
              {{ g.label }}<b v-if="g.key !== 'TODOS'"> {{ historyCount(g) }}</b>
            </span>
          </div>
          <table class="hist-table" v-if="historyRows.length">
            <thead><tr><th>Quando</th><th>Empresa</th><th>Evento</th><th>Detalhe</th><th>Quem</th></tr></thead>
            <tbody>
              <tr v-for="e in historyPage" :key="e.contextCode + '-' + e.id" :class="'ev-' + (e.type || '').toLowerCase()">
                <td class="nowrap">{{ fmtDateTime(e.createdAt) }}</td>
                <td><a @click="openDrawerById(e.subscriptionId)">{{ e.companyName || e.subscriptionId }}</a></td>
                <td class="nowrap">{{ eventLabel(e.type) }}</td>
                <td>{{ e.message }}</td>
                <td class="nowrap">{{ e.whoName }}</td>
              </tr>
            </tbody>
          </table>
          <p v-else class="empty">{{ history.loading ? 'Carregando...' : (history.loaded ? 'Nenhum evento para este filtro.' : 'Clique em Carregar histórico.') }}</p>
          <div class="hist-more" v-if="historyRows.length > history.limit">
            <button class="btn-secondary" @click="history.limit += 200">Mostrar mais ({{ historyRows.length - history.limit }} restantes)</button>
          </div>
        </div>

        <div class="action-bar" v-if="selectedCount">
          <span class="ab-count"><strong>{{ selectedCount }}</strong> selecionada(s) · MRR {{ money(selectedMrr) }}
            <span v-if="selectedOutsideFilter" class="ab-warn">· {{ selectedOutsideFilter }} fora do filtro atual <a @click="keepOnlyFiltered">manter só as do filtro</a></span>
          </span>
          <button class="btn-secondary" :disabled="runner.active" @click="startSync(selectedIds)">Ler IUGU</button>
          <button class="btn-secondary" :disabled="runner.active" @click="openSimulate(selectedIds)">Simular</button>
          <button class="btn-primary" :disabled="runner.active" @click="openMigrate(selectedIds)">Migrar...</button>
          <button class="btn-secondary" @click="openCardLinks(selectedIds)">Link de cartão</button>
          <button class="btn-secondary" @click="openReconcile(selectedIds)">Reconciliar</button>
          <button class="btn-secondary" @click="openRelink(selectedIds)" title="Descobre pela fatura IUGU que o Master recebeu qual assinatura IUGU cobra o cliente e corrige o vínculo">Corrigir vínculo IUGU</button>
          <button class="btn-secondary" @click="openWave(selectedIds)">Leva de comunicação</button>
          <button class="btn-secondary" @click="openExclude(selectedIds)">Excluir da migração</button>
          <button class="btn-secondary" @click="include(selectedIds)">Reincluir</button>
          <button class="btn-danger" :disabled="runner.active" @click="openRollback(selectedIds)">Reverter...</button>
          <button class="btn-secondary" @click="openExport">Exportar planilha</button>
          <button class="btn-link" @click="clearSelection">Limpar</button>
        </div>

        <div class="modal-overlay" v-if="modal.type" @click.self="closeModal">
          <div class="modal-card" :class="{ wide: modal.type === 'simulate' || modal.type === 'csv' || modal.type === 'paste' }">
            <div class="modal-head">
              <h4>{{ modalTitle }}</h4>
              <button class="modal-x" @click="closeModal">×</button>
            </div>

            <div class="modal-body">

              <template v-if="modal.type === 'simulate'">
                <div v-if="modal.running" class="bar"><div class="bar-fill" :style="{ width: pct(modal.done, modal.ids.length) + '%' }"></div></div>
                <p v-if="modal.retryMessage" class="txt-warn">{{ modal.retryMessage }}</p>
                <p v-if="modal.running">Simulando {{ modal.done }} de {{ modal.ids.length }} (lê a IUGU na hora, nada é criado)...<template v-if="modal.deferredCount"> {{ modal.deferredCount }} ficaram para a próxima rodada (IUGU/ASAAS lentos) e serão simuladas de novo.</template></p>
                <template v-if="!modal.running && modal.plans.length">
                  <div class="sim-summary">
                    <div class="sim-box ok"><span>{{ simReady.length }}</span>prontas para migrar</div>
                    <div class="sim-box warn"><span>{{ simWarn }}</span>prontas com aviso</div>
                    <div class="sim-box err"><span>{{ simBlocked.length }}</span>bloqueadas</div>
                    <div class="sim-box"><span>{{ money(simMrr) }}</span>MRR das prontas</div>
                  </div>
                  <div v-if="simBlockedGroups.length" class="sim-groups">
                    <strong>Bloqueios:</strong>
                    <span v-for="g in simBlockedGroups" :key="g.code" class="tag tag-err">{{ errorLabel(g.code) }} ({{ g.count }})</span>
                  </div>
                  <table class="result-table">
                    <thead><tr><th>Empresa</th><th>Resultado</th><th class="num">Master</th><th class="num">ASAAS</th><th>1º venc. ASAAS</th><th>Avisos / motivo</th></tr></thead>
                    <tbody>
                      <tr v-for="p in modal.plans" :key="p.subscriptionId" :class="p.canMigrate ? (p.warnings.length ? 'st-warn' : 'st-ok') : 'st-err'">
                        <td>{{ p.companyName }}<div v-if="p.asaasCustomerName" class="muted">cliente ASAAS: {{ p.asaasCustomerName }}</div><div v-if="p.iuguPaymentKind" class="muted">pagamento IUGU: {{ payLabel(p.iuguPaymentKind) }}</div></td>
                        <td>{{ p.canMigrate ? 'Pronta' : 'Bloqueada' }}</td>
                        <td class="num">{{ money(p.mrrMaster) }}</td>
                        <td class="num">{{ p.asaasValue ? money(p.asaasValue) : '—' }}</td>
                        <td>{{ p.asaasNextDueDate ? fmtDate(p.asaasNextDueDate) : '—' }}<span v-if="p.dueDayCapped" class="tag tag-info">27</span></td>
                        <td>
                          <span v-if="!p.canMigrate" class="txt-danger">{{ errorLabel(p.errorCode) }}: {{ p.errorMessage }}</span>
                          <div v-for="(w, i) in p.warnings" :key="i">{{ w }}</div>
                          <div v-if="p.canMigrate && p.customerExists" class="muted">Cliente ASAAS existente: {{ p.existingAsaasCustomerId }}</div>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </template>
              </template>

              <template v-if="modal.type === 'migrate'">
                <div class="confirm-summary">
                  <div><span>{{ modal.ids.length }}</span>assinaturas</div>
                  <div><span>{{ money(modalMrr) }}</span>MRR (IUGU quando lido)</div>
                  <div><span>{{ modal.unsynced }}</span>sem dado IUGU (serão lidas na hora)</div>
                </div>
                <p>Para cada assinatura, nesta ordem e retomando de onde parou se algo falhar:</p>
                <ol class="howto">
                  <li>Relê a assinatura no Master e na IUGU (valor e vencimento reais).</li>
                  <li>Cria ou reaproveita o cliente na conta <strong>ASAAS MAM</strong> (busca pelo CNPJ). Cliente novo leva o mesmo nome do cliente na IUGU.</li>
                  <li v-if="segment === 'NAO_MENSAL'">Cria a assinatura <strong>BOLETO no ciclo da IUGU</strong> (bimestral, trimestral, semestral ou anual) com o valor e o vencimento da IUGU (após o dia 27 → dia 27; se a IUGU já gerou o boleto do ciclo, começa no ciclo seguinte).</li>
                  <li v-else>Cria a assinatura <strong>BOLETO mensal</strong> com o valor da IUGU e o vencimento da IUGU (após o dia 27 → dia 27; se a IUGU já gerou o boleto do ciclo, começa no mês seguinte).</li>
                  <li v-if="segment === 'NAO_MENSAL'">Grava os IDs no Master. <strong>O valor e a periodicidade do plano no Master não mudam.</strong></li>
                  <li v-else>Grava os IDs no Master e, se o MRR do Master for diferente, assume o valor da IUGU (antes/depois fica registrado).</li>
                  <li>Suspende a assinatura na IUGU. Boletos IUGU já emitidos continuam válidos.</li>
                </ol>
                <p class="hint">Recomendado: <a @click="switchToSimulate">simular antes</a>. Itens com erro ficam marcados com o motivo e não travam a fila.</p>
                <div class="force-box" v-if="modal.suspendedCount || modal.forceSuspended">
                  <label class="opt"><input type="checkbox" v-model="modal.forceSuspended"/> <strong>Forçar a migração</strong>: IUGU suspensa/inativa, assinatura IUGU errada no Master ou vencimento IUGU já passado ({{ modal.suspendedCount }} da seleção)</label>
                  <template v-if="modal.forceSuspended">
                    <p class="txt-warn">Suspensa/inativa normalmente é cliente inadimplente ou que cancelou: ao migrar, o ASAAS volta a cobrar. Confirme com o financeiro. Se o vencimento da IUGU já passou, a 1ª cobrança vai para o próximo vencimento do cliente (mesmo dia, corte do dia 27); débitos antigos continuam na IUGU.</p>
                    <p class="txt-warn" v-if="modal.staleLinkCount">{{ modal.staleLinkCount }} da seleção com assinatura IUGU errada no Master: a IUGU pode seguir cobrando por outra assinatura ou fatura avulsa. Cancele essa cobrança na IUGU, senão o cliente paga nos dois.</p>
                    <input type="text" v-model="modal.forceReason" maxlength="400" placeholder="Motivo (obrigatório), ex.: financeiro confirmou que o cliente segue ativo"/>
                  </template>
                  <p v-else class="hint">Sem marcar, as suspensas voltam com "IUGU já suspensa", as de vínculo errado com "Assinatura IUGU errada no Master" e as de vencimento passado com "Vencimento passado".</p>
                </div>
                <div class="form-row">
                  <label>Leva de comunicação (opcional)</label>
                  <input type="text" v-model="modal.wave" maxlength="100" list="mig-waves" placeholder="ex.: Fatura 10/2026 paga na IUGU"/>
                  <datalist id="mig-waves"><option v-for="w in waves" :key="w" :value="w"></option></datalist>
                  <p class="hint">Marca estas empresas para o CX separar o comunicado (filtro "Leva de comunicação" e relatório de comunicação).</p>
                </div>
                <div class="form-row">
                  <label>Digite <strong>{{ confirmWord }}</strong> para confirmar</label>
                  <input type="text" v-model="modal.confirmText" :placeholder="confirmWord"/>
                </div>
              </template>

              <template v-if="modal.type === 'exclude'">
                <p>{{ modal.ids.length }} assinatura(s) deixam de contar na migração. Itens que já criaram algo no ASAAS não são excluídos (use Reverter).</p>
                <div class="form-row">
                  <label>Motivo (aparece na tela e no histórico)</label>
                  <input type="text" v-model="modal.reason" placeholder="ex.: cliente em negociação de cancelamento"/>
                </div>
              </template>

              <template v-if="modal.type === 'rollback'">
                <p>{{ modal.ids.length }} assinatura(s). Para cada uma com assinatura ASAAS: cancela as cobranças ASAAS em aberto, inativa a assinatura ASAAS, limpa o vínculo no Master, devolve o MRR anterior (se foi ajustado) e reativa a IUGU.</p>
                <p class="txt-warn">Ao reativar, a IUGU pode gerar fatura imediatamente se o vencimento já passou.</p>
                <div class="form-row">
                  <label>Motivo</label>
                  <input type="text" v-model="modal.reason" placeholder="ex.: migrado por engano"/>
                </div>
                <div class="form-row">
                  <label>Digite <strong>REVERTER</strong> para confirmar</label>
                  <input type="text" v-model="modal.confirmText" placeholder="REVERTER"/>
                </div>
                <div v-if="modal.result" class="modal-result">
                  <p>{{ modal.result.ok }} revertida(s), {{ modal.result.failed }} com problema (veja o histórico de cada uma).</p>
                </div>
              </template>

              <template v-if="modal.type === 'export'">
                <div class="form-row">
                  <label>O que exportar</label>
                  <label class="opt"><input type="radio" value="filtro" v-model="modal.exportScope"/> As {{ filteredRows.length }} do filtro atual</label>
                  <label class="opt" v-if="selectedCount"><input type="radio" value="selecao" v-model="modal.exportScope"/> As {{ selectedCount }} selecionadas</label>
                </div>
                <div class="form-row">
                  <label>Formato</label>
                  <label class="opt"><input type="radio" value="xlsx" v-model="modal.exportFormat"/> Excel (.xlsx)</label>
                  <label class="opt"><input type="radio" value="csv" v-model="modal.exportFormat"/> CSV (;)</label>
                </div>
                <label class="opt"><input type="checkbox" v-model="modal.exportRefresh" :disabled="runner.active"/> Buscar valor e vencimento <strong>na IUGU agora</strong> antes de gerar (recomendado)</label>
                <p class="hint" v-if="modal.exportRefresh">
                  Vai consultar {{ exportRefreshCount }} assinatura(s) na IUGU, uma a uma, respeitando o limite de requisições (estimativa: ~{{ exportEstimate }}).
                  O arquivo baixa sozinho no fim; dá para acompanhar no painel de progresso.
                </p>
                <p class="hint" v-else>Usa o último dado lido da IUGU de cada assinatura. Quem nunca foi lida sai com "não lido na IUGU".</p>
                <p class="hint" v-if="runner.active">Há uma execução em andamento: aguarde terminar para buscar na IUGU.</p>
                <p class="hint">Colunas: Id da empresa, Id assinatura, Nome, Link master, MRR assinatura, MRR assinatura IUGU, Data vencimento IUGU, Telefone, Email, Email financeiro, Ultimo boleto gerado.</p>
              </template>

              <template v-if="modal.type === 'csv'">
                <p>Arquivo <strong>{{ modal.fileName }}</strong>: {{ modal.csvTotal }} linha(s) lidas pela coluna <strong>{{ modal.csvColumn }}</strong>.</p>
                <div class="sim-summary">
                  <div class="sim-box ok"><span>{{ modal.matched.length }}</span>encontradas na base</div>
                  <div class="sim-box err"><span>{{ modal.unmatched.length }}</span>não encontradas</div>
                </div>
                <div v-if="modal.unmatched.length" class="unmatched">
                  <strong>Não encontradas:</strong> {{ modal.unmatched.slice(0, 50).join(', ') }}<span v-if="modal.unmatched.length > 50"> e mais {{ modal.unmatched.length - 50 }}</span>
                </div>
                <p class="hint">Aceita CSV com coluna "CNPJ", "Id assinatura"/"SubscriptionId" ou "Id da empresa"/"CompanyId" (separador ; ou ,). Sem cabeçalho, cada valor é procurado como Id assinatura, Id da empresa ou CNPJ. Uma empresa ou CNPJ seleciona todas as suas assinaturas na base.</p>
              </template>

              <template v-if="modal.type === 'card'">
                <p><strong>{{ modal.ids.length }}</strong> assinatura(s) vão receber um link de cadastro de cartão do ASAAS.</p>
                <ol class="howto">
                  <li>A fila relê a IUGU (valor e vencimento reais) e aplica as mesmas regras do boleto: após o dia 27 vai para o dia 27; se a IUGU já gerou a cobrança do ciclo, começa no mês seguinte.</li>
                  <li>Cria (ou reaproveita) o cliente no ASAAS MAM com o nome da IUGU e gera o link: o cliente cadastra o cartão e a assinatura nasce no ASAAS com a 1ª cobrança nessa data.</li>
                  <li><strong>A IUGU continua ativa</strong> enquanto o cliente não cadastrar. Quando cadastrar, a migração termina sozinha (Master, IUGU suspensa, boleto vinculado).</li>
                  <li>O link vale <strong>24 horas</strong> (limite do ASAAS). Se expirar ou o ASAAS recusar, a empresa volta com erro, o time recebe a nota no funil e dá para gerar outro link.</li>
                </ol>
                <div class="form-row">
                  <label>Nome do lote</label>
                  <input type="text" v-model="modal.batch" maxlength="100" placeholder="ex.: Lote 1 - cartão - Ana"/>
                </div>
                <div class="form-row first-charge">
                  <label>1ª cobrança no cartão</label>
                  <label class="opt"><input type="radio" value="AUTO" v-model="modal.firstChargeMode"/> Automática: próximo vencimento que ainda não tem boleto IUGU</label>
                  <label class="opt"><input type="radio" value="NEXT_CYCLE" v-model="modal.firstChargeMode"/> Pular para o ciclo seguinte (o cliente paga o atual na IUGU)</label>
                  <label class="opt"><input type="radio" value="DATE" v-model="modal.firstChargeMode"/> Data escolhida
                    <input v-if="modal.firstChargeMode === 'DATE'" type="date" v-model="modal.firstChargeDate" :min="tomorrowIso"/>
                  </label>
                  <p class="txt-danger" v-if="firstChargeProblem">{{ firstChargeProblem }}</p>
                  <p class="hint">Em qualquer opção, se a IUGU gerar o boleto do mesmo ciclo antes de o cliente cadastrar o cartão, a cobrança do cartão desse ciclo é removida sozinha e ele começa no ciclo seguinte (fica no histórico e no funil). Data escolhida que cai perto de um boleto IUGU já gerado é recusada.</p>
                </div>
                <p v-if="modal.ids.length > 30" class="txt-warn">São {{ modal.ids.length }} links que vencem em 24h. Gere só o que o time consegue enviar hoje; o restante fica para outro lote.</p>
                <p class="hint">Os links aparecem no quadro "Links de cartão" (abaixo da listagem), separados por lote e etapa: prontos para enviar, enviados, vencendo, expirados, com falha e cadastrados. Marque "enviado" ao mandar para o cliente.</p>
              </template>

              <template v-if="modal.type === 'reconcile'">
                <div v-if="modal.running" class="bar"><div class="bar-fill" :style="{ width: pct(modal.done, modal.ids.length) + '%' }"></div></div>
                <p v-if="modal.running">Reconciliando {{ modal.done }} de {{ modal.ids.length }}...</p>
                <p v-if="modal.retryMessage" class="txt-warn">{{ modal.retryMessage }}</p>
                <template v-if="!modal.running && modal.reconcile.length">
                  <div class="sim-summary">
                    <div class="sim-box ok"><span>{{ reconcileFixed }}</span>com correção feita</div>
                    <div class="sim-box"><span>{{ reconcileClean }}</span>já estavam certas</div>
                    <div class="sim-box err"><span>{{ reconcileProblems }}</span>precisam de ação</div>
                  </div>
                </template>
                <table class="result-table" v-if="modal.reconcile.length">
                  <thead><tr><th>Empresa</th><th>Corrigido agora</th><th>Precisa de ação</th></tr></thead>
                  <tbody>
                    <tr v-for="r in modal.reconcile" :key="r.subscriptionId" :class="r.problems.length ? 'st-err' : (r.fixed.length ? 'st-warn' : 'st-ok')">
                      <td><a @click="openDrawerById(r.subscriptionId)">{{ r.companyName }}</a></td>
                      <td><div v-for="(t, i) in r.fixed" :key="i">{{ t }}</div><span v-if="!r.fixed.length" class="muted">—</span></td>
                      <td><div v-for="(t, i) in r.problems" :key="i" class="txt-danger">{{ t }}</div><span v-if="!r.problems.length" class="muted">—</span></td>
                    </tr>
                  </tbody>
                </table>
              </template>

              <template v-if="modal.type === 'relink'">
                <ol class="howto" v-if="modal.relinkPhase === 'analyze'">
                  <li>Para cada assinatura, pego o último boleto IUGU que o Master recebeu e pergunto à IUGU de qual assinatura ele é.</li>
                  <li>Se for outra assinatura do <strong>mesmo cliente IUGU</strong>, que não esteja vinculada a outra empresa, ela fica pronta para corrigir.</li>
                  <li>Ao corrigir, troco o IUGUSubscriptionId no Master, releio a IUGU a partir da assinatura certa e registro no histórico e no funil. Nada é criado no ASAAS e nada é suspenso na IUGU.</li>
                </ol>
                <div v-if="modal.running" class="bar"><div class="bar-fill" :style="{ width: pct(modal.done, modal.relinkTotal) + '%' }"></div></div>
                <p v-if="modal.running">{{ modal.relinkPhase === 'apply' ? 'Corrigindo' : 'Analisando' }} {{ modal.done }} de {{ modal.relinkTotal }}...</p>
                <p v-if="modal.retryMessage" class="txt-warn">{{ modal.retryMessage }}</p>
                <div class="sim-summary" v-if="modal.relink.length">
                  <div class="sim-box ok" v-if="modal.relinkPhase === 'done'"><span>{{ relinkCount('CORRIGIDA') }}</span>corrigidas</div>
                  <div class="sim-box ok" v-else><span>{{ relinkFixable.length }}</span>prontas para corrigir</div>
                  <div class="sim-box"><span>{{ relinkCount('JA_CORRETO') }}</span>vínculo já certo</div>
                  <div class="sim-box err" v-if="relinkCount('MIGRADA_IUGU_REAL_ATIVA')"><span>{{ relinkCount('MIGRADA_IUGU_REAL_ATIVA') }}</span>migradas com a IUGU real ativa</div>
                  <div class="sim-box warn"><span>{{ relinkManual }}</span>resolver à mão</div>
                </div>
                <table class="result-table" v-if="modal.relink.length">
                  <thead><tr><th>Empresa</th><th>Vinculada hoje</th><th>Assinatura que cobra</th><th>Situação dela</th><th>Resultado</th></tr></thead>
                  <tbody>
                    <tr v-for="r in modal.relink" :key="r.subscriptionId" :class="relinkClass(r)">
                      <td><a @click="openDrawerById(r.subscriptionId)">{{ r.companyName }}</a><div class="sub muted">{{ r.contextCode }} · {{ statusLabelOf(r.status) }}</div></td>
                      <td class="ids">{{ r.currentIuguSubscriptionId || '—' }}</td>
                      <td class="ids">{{ r.realIuguSubscriptionId || '—' }}<div class="sub muted" v-if="r.evidenceInvoiceId">boleto {{ fmtDate(r.evidenceDueDate) }}</div></td>
                      <td class="nowrap">
                        <template v-if="r.realIuguSubscriptionId && r.realIuguSubscriptionId !== r.currentIuguSubscriptionId && (r.realActive !== null || r.realSuspended !== null)">
                          <span :class="r.realSuspended || r.realActive === false ? 'txt-danger' : 'ok-text'">{{ r.realSuspended ? 'suspensa' : (r.realActive === false ? 'inativa' : 'ativa') }}</span>
                          <div class="sub muted">{{ money(r.realMrr) }} · venc. {{ fmtDate(r.realExpiresAt) }}</div>
                        </template>
                        <span v-else class="muted">—</span>
                      </td>
                      <td>{{ r.message }}<div v-if="r.technicalDetail" class="sub"><a class="laudo-link" @click="copyText(r.technicalDetail, 'Detalhe copiado')">copiar detalhe técnico</a></div></td>
                    </tr>
                  </tbody>
                </table>
                <div class="form-row" v-if="modal.relinkPhase === 'analyze' && !modal.running && relinkFixable.length">
                  <label>Digite CORRIGIR para trocar o vínculo das {{ relinkFixable.length }} prontas</label>
                  <input type="text" v-model="modal.confirmText" placeholder="CORRIGIR"/>
                </div>
              </template>

              <template v-if="modal.type === 'wave'">
                <p><strong>{{ modal.ids.length }}</strong> empresa(s) selecionada(s).</p>
                <div class="form-row">
                  <label>Nome da leva</label>
                  <input type="text" v-model="modal.wave" maxlength="100" list="mig-waves-2" placeholder="ex.: Fatura 10/2026 paga na IUGU"/>
                  <datalist id="mig-waves-2"><option v-for="w in waves" :key="w" :value="w"></option></datalist>
                </div>
                <p class="hint">A leva separa o comunicado do CX: filtre por ela na listagem e gere o relatório de comunicação só dela. Deixe em branco e clique em "Remover da leva" para tirar.</p>
              </template>

              <template v-if="modal.type === 'laudo'">
                <p class="hint">Copie e cole este laudo para quem for analisar (inclusive para o Claude). Ele traz a chamada, a resposta da API e a situação da fila.</p>
                <pre class="laudo-box">{{ modal.laudo }}</pre>
              </template>

              <template v-if="modal.type === 'laudos'">
                <table class="result-table">
                  <thead><tr><th>Quando</th><th>O que falhou</th><th></th></tr></thead>
                  <tbody>
                    <tr v-for="l in laudos" :key="l.id">
                      <td>{{ l.time }}</td>
                      <td>{{ l.title }}</td>
                      <td><a @click="openIncident(l.id)">abrir laudo</a></td>
                    </tr>
                  </tbody>
                </table>
              </template>

              <template v-if="modal.type === 'paste'">
                <p>Cole a coluna copiada da planilha: um CNPJ, Id da empresa ou Id assinatura por linha (pode colar com o cabeçalho).</p>
                <textarea class="paste-box" v-model="modal.pasteText" rows="12" placeholder="12.345.678/0001-90&#10;98765432000110&#10;..."></textarea>
              </template>

            </div>

            <div class="modal-foot">
              <button class="btn-secondary" @click="closeModal">Fechar</button>
              <template v-if="modal.type === 'simulate' && !modal.running">
                <button class="btn-secondary" :disabled="!modal.plans.length" @click="exportSimulation">Exportar simulação</button>
                <button class="btn-primary" :disabled="!simReady.length" @click="migrateReady">Migrar as {{ simReady.length }} prontas...</button>
              </template>
              <button v-if="modal.type === 'migrate'" class="btn-primary" :disabled="modal.confirmText !== confirmWord || modal.running || (modal.forceSuspended && modal.forceReason.trim().length < 10)" @click="confirmMigrate">
                {{ modal.running ? 'Enviando...' : 'Enviar para a fila e migrar' }}
              </button>
              <button v-if="modal.type === 'exclude'" class="btn-primary" :disabled="modal.running" @click="confirmExclude">Excluir</button>
              <button v-if="modal.type === 'rollback' && !modal.result" class="btn-danger" :disabled="modal.confirmText !== 'REVERTER' || modal.running" @click="confirmRollback">
                {{ modal.running ? 'Revertendo...' : 'Reverter' }}
              </button>
              <button v-if="modal.type === 'export'" class="btn-primary" :disabled="(modal.exportRefresh && runner.active) || !exportRows.length" @click="confirmExport">
                {{ modal.exportRefresh ? 'Buscar na IUGU e gerar' : 'Gerar arquivo' }}
              </button>
              <button v-if="modal.type === 'card'" class="btn-primary" :disabled="modal.running || !modal.ids.length || !!firstChargeProblem" @click="confirmCardLinks">{{ modal.running ? 'Enviando...' : 'Gerar links de cartão' }}</button>
              <button v-if="modal.type === 'reconcile' && !modal.running && modal.reconcile.length" class="btn-secondary" @click="copyText(reconcileText, 'Relatório copiado')">Copiar relatório</button>
              <template v-if="modal.type === 'relink' && !modal.running && modal.relink.length">
                <button class="btn-secondary" @click="copyText(relinkText, 'Relatório copiado')">Copiar relatório</button>
                <button v-if="modal.relinkPhase === 'analyze'" class="btn-primary" :disabled="!relinkFixable.length || modal.confirmText !== 'CORRIGIR'" @click="confirmRelink">Corrigir {{ relinkFixable.length }} vínculo(s)</button>
              </template>
              <template v-if="modal.type === 'wave'">
                <button class="btn-secondary" :disabled="modal.running" @click="confirmWave(true)">Remover da leva</button>
                <button class="btn-primary" :disabled="modal.running || !modal.wave.trim()" @click="confirmWave(false)">Definir leva</button>
              </template>
              <button v-if="modal.type === 'laudo'" class="btn-primary" @click="copyText(modal.laudo, 'Laudo copiado')">Copiar laudo</button>
              <button v-if="modal.type === 'paste'" class="btn-primary" :disabled="!modal.pasteText.trim()" @click="confirmPaste">Procurar na base</button>
              <template v-if="modal.type === 'csv'">
                <button class="btn-secondary" :disabled="!modal.matched.length" @click="csvSelect">Selecionar as {{ modal.matched.length }}</button>
                <button class="btn-secondary" :disabled="!modal.matched.length" @click="csvExclude">Excluir as {{ modal.matched.length }}</button>
                <button class="btn-primary" :disabled="!modal.matched.length" @click="csvSimulate">Simular as {{ modal.matched.length }}</button>
              </template>
            </div>
          </div>
        </div>

        <div class="drawer-overlay" v-if="drawer.item" @click.self="drawer.item = null">
          <div class="drawer">
            <div class="drawer-head">
              <div>
                <h4>{{ drawer.item.companyName }}</h4>
                <span class="badge" :class="'b-' + statusKey(drawer.item)">{{ statusLabel(drawer.item) }}</span>
              </div>
              <button class="modal-x" @click="drawer.item = null">×</button>
            </div>
            <div class="drawer-body">
              <div v-if="drawer.item.status === 'ERRO'" class="banner banner-err">
                <strong>{{ errorLabel(drawer.item.errorCode) }}</strong>: {{ drawer.item.errorMessage }}
                <div class="hint">{{ errorHelp(drawer.item.errorCode) }}</div>
              </div>
              <dl class="kv">
                <dt>Id da empresa</dt><dd>{{ drawer.item.companyId }}</dd>
                <dt>Id assinatura</dt><dd>{{ drawer.item.subscriptionId }}</dd>
                <dt>Banco</dt><dd>{{ drawer.item.contextCode }}</dd>
                <dt>Master</dt><dd><a :href="drawer.item.masterUrl" target="_blank">abrir empresa</a></dd>
                <dt>CNPJ</dt><dd>{{ drawer.item.cnpj }}</dd>
                <dt>Telefone</dt><dd>{{ drawer.item.phone || '—' }}</dd>
                <dt>E-mail</dt><dd>{{ drawer.item.email || '—' }}</dd>
                <dt>E-mail financeiro</dt><dd>{{ drawer.item.financialEmail || '—' }}</dd>
                <dt>MRR Master</dt><dd>{{ money(drawer.item.mrrMaster) }}<template v-if="drawer.item.mrrAdjusted"> (antes {{ money(drawer.item.mrrBefore) }} → depois {{ money(drawer.item.mrrAfter) }})</template></dd>
                <dt>IUGU</dt><dd>{{ drawer.item.iuguSubscriptionId }} · {{ drawer.item.iuguSyncStatus === 'OK' ? 'lida em ' + fmtDateTime(drawer.item.iuguSyncedAt) : (drawer.item.iuguSyncError || 'não lida') }}</dd>
                <dt>Última fatura paga IUGU</dt><dd>{{ drawer.item.iuguPaidDueDate ? 'vencimento ' + fmtDate(drawer.item.iuguPaidDueDate) : '—' }}</dd>
                <template v-if="drawer.item.forceIuguSuspended"><dt>Migração forçada</dt><dd>IUGU suspensa/inativa · {{ drawer.item.forceBy }} em {{ fmtDateTime(drawer.item.forceAt) }} · {{ drawer.item.forceReason }}</dd></template>
                <dt>Leva de comunicação</dt><dd>{{ drawer.item.commsWave ? drawer.item.commsWave + ' · ' + (drawer.item.commsWaveBy || '') + ' em ' + fmtDateTime(drawer.item.commsWaveAt) : '—' }}</dd>
                <dt>Pagamento IUGU</dt><dd>{{ payTitle(drawer.item) || '—' }}<template v-if="payDiverges(drawer.item)"> · no Master está como {{ drawer.item.paymentMethod === 2 ? 'cartão' : 'boleto' }}</template></dd>
                <dt>MRR IUGU</dt><dd>{{ drawer.item.iuguMrr !== null && drawer.item.iuguMrr !== undefined ? money(drawer.item.iuguMrr) : '—' }}</dd>
                <dt>Venc. IUGU</dt><dd>{{ drawer.item.iuguExpiresAt ? fmtDate(drawer.item.iuguExpiresAt) : '—' }}<template v-if="drawer.item.iuguSuspended"> · suspensa</template></dd>
                <dt>Último boleto IUGU</dt><dd><template v-if="drawer.item.iuguLastInvoiceDueDate">{{ fmtDate(drawer.item.iuguLastInvoiceDueDate) }} · {{ invoiceStatusLabel(drawer.item.iuguLastInvoiceStatus) }} · {{ drawer.item.iuguLastInvoiceTotal }} <a v-if="drawer.item.iuguLastInvoiceUrl" :href="drawer.item.iuguLastInvoiceUrl" target="_blank">ver</a></template><template v-else>—</template></dd>
                <dt>Último boleto no Master</dt><dd>{{ drawer.item.localLastBoletoDueDate ? fmtDate(drawer.item.localLastBoletoDueDate) : '—' }}</dd>
                <dt>Cliente ASAAS</dt><dd><a v-if="drawer.item.asaasCustomerId" :href="asaasCliLink(drawer.item)" target="_blank">{{ drawer.item.asaasCustomerId }}</a><template v-else>—</template></dd>
                <dt>Assinatura ASAAS</dt><dd><template v-if="drawer.item.asaasSubscriptionId"><a :href="asaasSubLink(drawer.item)" target="_blank">{{ drawer.item.asaasSubscriptionId }}</a> · {{ money(drawer.item.asaasValue) }} · 1º venc. {{ fmtDate(drawer.item.asaasNextDueDate) }}</template><template v-else>—</template></dd>
                <dt>1º boleto ASAAS</dt><dd><a v-if="drawer.item.asaasFirstPaymentUrl" :href="drawer.item.asaasFirstPaymentUrl" target="_blank">abrir boleto</a><template v-else>—</template></dd>
                <dt>Etapa concluída</dt><dd>{{ stepLabel(drawer.item.step) }}</dd>
                <dt>Migrada</dt><dd>{{ drawer.item.migratedAt ? fmtDateTime(drawer.item.migratedAt) + ' por ' + drawer.item.migratedBy : '—' }}</dd>
                <template v-if="cardOf(drawer.item)">
                  <dt>Link de cartão</dt>
                  <dd>
                    <span :class="cardClass(cardOf(drawer.item))">{{ cardSummary(cardOf(drawer.item)) }}</span>
                    <template v-if="cardOf(drawer.item).link"><br/><a :href="cardOf(drawer.item).link" target="_blank">{{ cardOf(drawer.item).link }}</a></template>
                    <template v-if="cardOf(drawer.item).value"><br/>R$ {{ num(cardOf(drawer.item).value) }}/mês · 1ª cobrança {{ fmtDate(cardOf(drawer.item).nextDueDate) }} · gerado {{ cardOf(drawer.item).linksGenerated }}x</template>
                    <template v-if="cardOf(drawer.item).lastError"><br/><span class="txt-danger">{{ cardOf(drawer.item).lastError }}</span></template>
                  </dd>
                </template>
              </dl>
              <div class="drawer-actions" v-if="cardOf(drawer.item) && cardOf(drawer.item).state === 'ATIVO'">
                <button class="btn-primary" @click="copyText(cardOf(drawer.item).link, 'Link copiado')">Copiar link</button>
                <button class="btn-secondary" @click="copyText(cardMessage(drawer.item), 'Mensagem copiada')">Copiar mensagem para o cliente</button>
              </div>
              <div class="drawer-actions">
                <button class="btn-secondary" :disabled="runner.active" @click="startSync([drawer.item.subscriptionId])">Ler IUGU</button>
                <button class="btn-secondary" :disabled="runner.active" @click="openSimulate([drawer.item.subscriptionId])">Simular</button>
                <button class="btn-primary" :disabled="runner.active" @click="openMigrate([drawer.item.subscriptionId])">Migrar...</button>
                <button v-if="drawer.item.status !== 'EXCLUIDA'" class="btn-secondary" @click="openExclude([drawer.item.subscriptionId])">Excluir</button>
                <button v-else class="btn-secondary" @click="include([drawer.item.subscriptionId])">Reincluir</button>
                <button v-if="drawer.item.asaasSubscriptionId" class="btn-danger" :disabled="runner.active" @click="openRollback([drawer.item.subscriptionId])">Reverter...</button>
                <button v-if="canRequestCard(drawer.item)" class="btn-secondary" @click="openCardLinks([drawer.item.subscriptionId])">{{ cardOf(drawer.item) ? 'Gerar novo link de cartão' : 'Gerar link de cartão' }}</button>
                <button v-if="drawer.item.status === 'MIGRADA'" class="btn-secondary" @click="openReconcile([drawer.item.subscriptionId])">Reconciliar</button>
                <button class="btn-secondary" @click="openRelink([drawer.item.subscriptionId])">Corrigir vínculo IUGU</button>
                <button class="btn-link" @click="copyItemLaudo(drawer.item)">Copiar laudo da empresa</button>
              </div>
              <h5>Histórico</h5>
              <p v-if="drawer.loading" class="muted">Carregando...</p>
              <ul class="timeline" v-else>
                <li v-for="e in drawer.events" :key="e.id" :class="'ev-' + (e.type || '').toLowerCase()">
                  <span class="t">{{ fmtDateTime(e.createdAt) }}</span>
                  <span class="who" v-if="e.whoName">{{ e.whoName }}</span>
                  <div>{{ e.message }}</div>
                </li>
                <li v-if="!drawer.events.length" class="muted">Sem eventos ainda.</li>
              </ul>
            </div>
          </div>
        </div>

      </template>
    </div>
  </section>
</template>

<script>
import {
  MIGRACAO_IUGU_ASAAS_ACESSO,
  MIGRACAO_IUGU_ASAAS_COORTE,
  MIGRACAO_IUGU_ASAAS_ITENS,
  MIGRACAO_IUGU_ASAAS_SYNC_IUGU,
  MIGRACAO_IUGU_ASAAS_SIMULAR,
  MIGRACAO_IUGU_ASAAS_EXCLUIR,
  MIGRACAO_IUGU_ASAAS_REINCLUIR,
  MIGRACAO_IUGU_ASAAS_ENFILEIRAR,
  MIGRACAO_IUGU_ASAAS_FILA_STATUS,
  MIGRACAO_IUGU_ASAAS_FILA_PAUSAR,
  MIGRACAO_IUGU_ASAAS_FILA_RETOMAR,
  MIGRACAO_IUGU_ASAAS_CARTAO_LINK,
  MIGRACAO_IUGU_ASAAS_CARTOES,
  MIGRACAO_IUGU_ASAAS_CARTAO_ENVIADO,
  MIGRACAO_IUGU_ASAAS_LEVA,
  MIGRACAO_IUGU_ASAAS_RECONCILIAR,
  MIGRACAO_IUGU_ASAAS_VINCULO_ANALISAR,
  MIGRACAO_IUGU_ASAAS_VINCULO_CORRIGIR,
  MIGRACAO_IUGU_ASAAS_REVERTER,
  MIGRACAO_IUGU_ASAAS_EVENTOS
} from "../../../../../urlConfig";
import { REQUEST_SERVICE } from "../../../../../requestConfig";
import { UTIL } from "../../../../../utils";
import topMenu from "../../../components/default/topMenu.vue";
import pageHeader from "../../../components/default/pageHeader.vue";
import swal from "sweetalert";

const PASSWORD_KEY = "iuguAsaasMigrationPwd";
const SYNC_CHUNK = 20;
const SIMULATE_CHUNK = 25;
const SIMULATE_MAX_RETRIES = 5;
const EVENT_LABELS = {
  COORTE: "Base atualizada",
  IUGU_SYNC: "Leitura IUGU",
  SIMULADA: "Simulação",
  EXCLUIDA: "Excluída da migração",
  REINCLUIDA: "Reincluída",
  ENFILEIRADA: "Enviada para a fila",
  CUSTOMER: "Cliente ASAAS",
  ASSINATURA: "Assinatura ASAAS",
  MRR_AJUSTADO: "MRR ajustado",
  VENCIMENTO_AJUSTADO: "Vencimento alterado",
  IUGU_SUSPENSA: "IUGU suspensa",
  MIGRADA: "Migrada",
  ERRO: "Erro",
  RETENTATIVA: "Nova tentativa",
  REVERTIDA: "Revertida",
  FILA_PAUSADA: "Fila pausada",
  CARTAO_SOLICITADO: "Link de cartão pedido",
  CARTAO_LINK: "Link de cartão gerado",
  CARTAO_CADASTRADO: "Cartão cadastrado",
  BOLETO_VINCULADO: "Boleto no Master",
  RECONCILIADA: "Reconciliação",
  CARTAO_ENVIADO: "Link enviado ao cliente",
  LEVA: "Leva de comunicação",
  MIGRACAO_FORCADA: "Migração forçada",
  VINCULO_IUGU: "Vínculo IUGU corrigido",
  COBRANCA_AJUSTADA: "1ª cobrança ajustada"
};
const HISTORY_GROUPS = [
  { key: "TODOS", label: "Tudo", types: null },
  { key: "MIGRADA", label: "Migradas", types: ["MIGRADA"] },
  { key: "VENC", label: "Vencimento alterado", types: ["VENCIMENTO_AJUSTADO"] },
  { key: "MRR", label: "MRR ajustado", types: ["MRR_AJUSTADO"] },
  { key: "ERRO", label: "Erros", types: ["ERRO"] },
  { key: "ASAAS", label: "Cliente/assinatura ASAAS", types: ["CUSTOMER", "ASSINATURA"] },
  { key: "IUGU", label: "IUGU suspensa", types: ["IUGU_SUSPENSA"] },
  { key: "SIM", label: "Simulações", types: ["SIMULADA"] },
  { key: "LEITURA", label: "Leituras IUGU", types: ["IUGU_SYNC"] },
  { key: "FILA", label: "Fila e retentativas", types: ["ENFILEIRADA", "RETENTATIVA", "FILA_PAUSADA"] },
  { key: "BASE", label: "Exclusões e base", types: ["EXCLUIDA", "REINCLUIDA", "COORTE"] },
  { key: "REV", label: "Reversões", types: ["REVERTIDA"] },
  { key: "CARTAO", label: "Cartão", types: ["CARTAO_SOLICITADO", "CARTAO_LINK", "CARTAO_ENVIADO", "CARTAO_CADASTRADO", "COBRANCA_AJUSTADA"] },
  { key: "BOLETO", label: "Boletos no Master e reconciliação", types: ["BOLETO_VINCULADO", "RECONCILIADA"] },
  { key: "LEVA", label: "Levas de comunicação", types: ["LEVA"] },
  { key: "VINCULO", label: "Vínculo IUGU", types: ["VINCULO_IUGU"] }
];
const SERVER_POLL_ACTIVE_MS = 5000;
const SERVER_POLL_IDLE_MS = 30000;
const SERVER_EVENT_TYPES = "MIGRADA,ERRO,RETENTATIVA,VENCIMENTO_AJUSTADO,MRR_AJUSTADO,IUGU_SUSPENSA,ASSINATURA,CUSTOMER,FILA_PAUSADA,CARTAO_LINK,CARTAO_CADASTRADO,BOLETO_VINCULADO";
const RECONCILE_CHUNK = 10;
const RELINK_CHUNK = 5;
const RELINK_CODES = ["IUGU_VINCULO_DIVERGENTE"];
const RELINK_MANUAL = ["SEM_BOLETO_IUGU", "FATURA_AVULSA", "CLIENTE_DIFERENTE", "VINCULADA_EM_OUTRA", "STATUS_NAO_PERMITE", "ASSINATURA_NAO_ENCONTRADA", "MASTER_MUDOU", "IUGU_INDISPONIVEL", "IUGU_ERRO", "ADIADA", "INESPERADO"];
const DAY_MS = 86400000;
let nameClickTimer = null;
const FORCE_CODES = ["IUGU_JA_SUSPENSA", "IUGU_INATIVA", "VENCIMENTO_PASSADO", "AGUARDAR_BOLETO_IUGU", "IUGU_VINCULO_DIVERGENTE"];
const CYCLE_LABEL = { 1: "mensal", 2: "bimestral", 3: "trimestral", 6: "semestral", 12: "anual", 24: "bianual" };
const PLAN_TYPE_LABEL = { 0: "sem periodicidade", 1: "mensal", 2: "bimestral", 3: "trimestral", 4: "semestral", 5: "anual", 6: "bianual" };
const CARD_CODES = ["CARTAO", "CARTAO_LINK_EXPIRADO", "CARTAO_LINK_FALHOU"];
const CARD_SEGMENTS = [
  { key: "PRONTOS", label: "Prontos para enviar" },
  { key: "VENCENDO", label: "Vencem em até 6h" },
  { key: "ENVIADOS", label: "Enviados, aguardando cadastro" },
  { key: "GERANDO", label: "Gerando" },
  { key: "EXPIRADOS", label: "Expirados" },
  { key: "FALHARAM", label: "Falharam" },
  { key: "CADASTRADOS", label: "Cadastrados" },
  { key: "TODOS", label: "Todos" }
];
const CARD_STATES = {
  SOLICITADO: "gerando link de cartão...",
  ATIVO: "link de cartão ativo",
  CADASTRADO: "cartão cadastrado",
  EXPIRADO: "link de cartão expirou",
  FALHOU: "link de cartão falhou"
};
let incidentSeq = 0;
const CARD_EXPIRING_HOURS = 6;
const PAY_LABEL = { CARTAO: "cartão", BOLETO: "boleto", PIX: "pix", INDEFINIDO: "não identificado" };

const ERRORS = {
  CNPJ_INVALIDO: ["CNPJ inválido", "Corrija o CNPJ da empresa/unidade no Master e reprocesse."],
  SEM_EMAIL: ["Sem e-mail", "Cadastre o e-mail financeiro da empresa no Master e reprocesse."],
  CARTAO: ["Paga com cartão", "Gere o link de cartão (botão abaixo) e envie ao cliente. A IUGU segue ativa até ele cadastrar o cartão."],
  CARTAO_LINK_EXPIRADO: ["Link de cartão expirou", "O cliente não cadastrou o cartão em 24h. Gere um novo link e reenvie; a IUGU segue ativa."],
  CARTAO_LINK_FALHOU: ["Link de cartão falhou", "O ASAAS recusou o link. Abra a empresa e copie o laudo; a IUGU segue ativa."],
  IUGU_JA_SUSPENSA: ["IUGU já suspensa", "Confirme com o financeiro. Se o cliente deve ser cobrado, use \"Migrar mesmo assim\" (com motivo)."],
  IUGU_VINCULO_DIVERGENTE: ["Assinatura IUGU errada no Master", "O Master recebe boletos deste cliente, mas a assinatura IUGU vinculada está parada: os boletos vêm de outra cobrança na IUGU. Corrija o vínculo (botão abaixo) e releia a IUGU antes de migrar. Se a cobrança da IUGU já foi tratada, use \"Migrar mesmo assim\" (com motivo)."],
  IUGU_INATIVA: ["IUGU inativa", "Assinatura inativa na IUGU: confirme a situação do cliente. Se deve ser cobrado, use \"Migrar mesmo assim\"."],
  IUGU_NAO_ENCONTRADA: ["IUGU não encontrada", "O id IUGU gravado no Master não existe. Corrija o vínculo e reprocesse."],
  IUGU_SEM_VALOR: ["IUGU sem valor", "A assinatura na IUGU não tem valor recorrente. Revise na IUGU."],
  IUGU_SEM_VENCIMENTO: ["IUGU sem vencimento", "A IUGU não informou o vencimento. Revise na IUGU."],
  IUGU_ERRO: ["Erro IUGU", "A IUGU recusou a leitura. Veja a mensagem e reprocesse."],
  IUGU_INDISPONIVEL: ["IUGU instável", "Leitura falhou por instabilidade; tentaremos de novo."],
  VENCIMENTO_PASSADO: ["Vencimento passado", "O vencimento da IUGU já passou sem boleto. Use \"Migrar mesmo assim\": a 1ª cobrança vai para o próximo vencimento do cliente."],
  ASAAS_CLIENTE: ["ASAAS recusou o cliente", "Geralmente CEP, telefone ou e-mail inválido. Corrija no Master e reprocesse."],
  ASAAS_ASSINATURA: ["ASAAS recusou a assinatura", "Veja a mensagem do ASAAS, corrija e reprocesse."],
  ASAAS_ASSINATURA_EXISTENTE: ["Já tem assinatura no ASAAS", "Há outra assinatura ativa para esse cliente no ASAAS. Verifique duplicidade antes de reprocessar."],
  IUGU_SUSPENSAO_FALHOU: ["IUGU não suspensa", "CRÍTICO: ASAAS criado e IUGU ativa. Reprocesse ou suspenda a IUGU manualmente."],
  INSTABILIDADE: ["Gateway instável", "Muitas falhas seguidas de IUGU/ASAAS. Reprocesse mais tarde."],
  NAO_MAM: ["Não é mais MAM", "Saiu do critério da migração. Normalmente basta excluir."],
  NAO_MENSAL: ["Não é mensal", "Migre pela aba Não mensais (o plano do Master não muda; só a recorrência da IUGU passa para o ASAAS)."],
  OUTRO_SEGMENTO: ["Aba errada", "Esta assinatura pertence à outra aba (Mensais / Não mensais). Simule e migre por lá."],
  CICLO_IUGU_DESCONHECIDO: ["Ciclo IUGU desconhecido", "Não deu para saber de quanto em quanto tempo a IUGU cobra. Confira o plano no painel da IUGU e releia."],
  CICLO_NAO_SUPORTADO: ["Ciclo sem equivalente no ASAAS", "A IUGU cobra num intervalo que o ASAAS não tem (só mensal, bimestral, trimestral, semestral e anual). Migre à mão."],
  CARTAO_DATA_INVALIDA: ["Data do cartão recusada", "A data escolhida para a 1ª cobrança no cartão já passou, passa do dia 27 ou cai no ciclo de um boleto IUGU já gerado. Gere o link de novo com outra data."],
  ASSINATURA_NAO_ATIVA: ["Assinatura não ativa", "Saiu do critério da migração. Normalmente basta excluir."],
  JA_TEM_ASAAS: ["Já tem ASAAS no Master", "A assinatura já tem ASAAS gravado por outro fluxo. Confira e exclua."],
  SEM_IUGU: ["Sem IUGU", "A assinatura não tem id IUGU no Master."],
  EMPRESA_DELETADA: ["Empresa deletada", "Exclua da migração."],
  ASSINATURA_NAO_ENCONTRADA: ["Assinatura sumiu", "A assinatura não existe mais no banco. Exclua da migração."],
  VALOR_INVALIDO: ["Valor inválido", "Acione o time de desenvolvimento com a mensagem."],
  JA_MIGRADA: ["Já migrada", "Nada a fazer."],
  EXCLUIDA: ["Excluída", "Reinclua na migração antes de simular ou migrar."],
  AGUARDAR_BOLETO_IUGU: ["Aguardar boleto IUGU", "Vencimento após o dia 27 com o dia 27 já passado: migre depois que a IUGU gerar o boleto deste ciclo, ou use \"Migrar mesmo assim\" para ir ao próximo vencimento."],
  IUGU_FATUROU_CICLO: ["IUGU e ASAAS no mesmo ciclo", "CRÍTICO: a IUGU gerou o boleto antes da suspensão e o ASAAS também vai cobrar. Cancele um dos dois."],
  ASAAS_CRIADO_INCOMPLETO: ["ASAAS criado, migração incompleta", "CRÍTICO: a assinatura ASAAS existe e a IUGU segue ativa. Reprocesse ou reverta."],
  REVERSAO_BLOQUEADA: ["Reversão bloqueada", "Veja o motivo (cobrança paga/vencida no ASAAS ou falha de gateway) e trate com o financeiro."],
  REVERSAO_INTERROMPIDA: ["Reversão interrompida", "CRÍTICO: a reversão parou no meio. Confira ASAAS e IUGU antes de agir."],
  INESPERADO: ["Erro inesperado", "Acione o time de desenvolvimento com a mensagem."]
};

const CRITICAL_CODES = ["IUGU_SUSPENSAO_FALHOU", "ASAAS_CRIADO_INCOMPLETO", "IUGU_FATUROU_CICLO", "REVERSAO_INTERROMPIDA"];

const STATUS_LABEL = {
  PENDENTE: "A migrar",
  NA_FILA: "Na fila",
  PROCESSANDO: "Processando",
  REVERTENDO: "Revertendo",
  MIGRADA: "Migrada",
  ERRO: "Erro",
  EXCLUIDA: "Excluída",
  REVERTIDA: "Revertida",
  AGUARDANDO_CARTAO: "Aguardando cartão"
};

const STEP_LABEL = {
  CUSTOMER: "Cliente ASAAS criado",
  ASSINATURA: "Assinatura ASAAS criada",
  BANCO: "Gravada no Master",
  IUGU_SUSPENSA: "IUGU suspensa"
};

let runnerSeq = 0;
let modalSeq = 0;
let loadSeq = 0;

function emptyRunner() {
  runnerSeq++;
  return {
    id: runnerSeq, active: false, finished: false, paused: false, inFlight: false, mode: "",
    total: 0, done: 0, ok: 0, failed: 0, retrying: 0, skipped: 0, throttleMs: 0,
    queue: [], retried: {}, idleRounds: 0, message: "", log: [], httpFailures: 0
  };
}

function emptyModal() {
  modalSeq++;
  return {
    id: modalSeq, type: "", ids: [], running: false, done: 0, plans: [], confirmText: "", reason: "",
    result: null, unsynced: 0, fileName: "", csvColumn: "", csvTotal: 0, matched: [], unmatched: [], pasteText: "",
    exportScope: "filtro", exportFormat: "xlsx", exportRefresh: true, queue: [], simRetries: {}, deferredCount: 0, httpFailures: 0, retryMessage: "",
    reconcile: [], laudo: "", batch: "", wave: "", forceSuspended: false, forceReason: "", suspendedCount: 0, staleLinkCount: 0,
    relink: [], relinkPhase: "", relinkTotal: 0, firstChargeMode: "AUTO", firstChargeDate: ""
  };
}

export default {
  name: "migracaoIuguAsaas",
  components: { topMenu, pageHeader },
  data() {
    return {
      password: "",
      passwordInput: "",
      authChecking: false,
      authError: "",
      loading: false,
      refreshing: false,
      refreshResult: null,
      items: [],
      shardsFailed: [],
      itemWarnings: [],
      cardPanel: { segment: "PRONTOS", batch: "", search: "", selected: {}, busy: false },
      filters: this.defaultFilters(),
      sort: { key: "iuguExpiresAt", dir: 1 },
      page: 1,
      pageSize: 100,
      selected: {},
      allItems: [],
      segment: "MENSAL",
      lastClickedIndex: null,
      runner: emptyRunner(),
      modal: emptyModal(),
      drawer: { item: null, events: [], loading: false },
      history: { events: [], loading: false, loaded: false, loadedAt: "", group: "TODOS", search: "", limit: 200 },
      cards: {},
      cardsError: "",
      laudos: [],
      server: { visible: false, status: null, events: [], busy: false, error: "", checkedAt: "", timer: null, lastDone: -1, polls: 0 },
      expiredNotified: false
    };
  },
  computed: {
    currentMonthKey() {
      const now = new Date();
      return now.getFullYear() + "-" + (now.getMonth() < 9 ? "0" : "") + (now.getMonth() + 1);
    },
    monthOptions() {
      const names = ["janeiro", "fevereiro", "março", "abril", "maio", "junho", "julho", "agosto", "setembro", "outubro", "novembro", "dezembro"];
      const now = new Date();
      const list = [];
      for (let back = 0; back < 4; back++) {
        const d = new Date(now.getFullYear(), now.getMonth() - back, 1);
        const key = d.getFullYear() + "-" + (d.getMonth() < 9 ? "0" : "") + (d.getMonth() + 1);
        list.push({ key: key, label: names[d.getMonth()] + "/" + d.getFullYear() + (back === 0 ? " (este mês)" : "") });
      }
      return list;
    },
    waves() {
      const set = {};
      this.items.forEach((i) => { if (i.commsWave) set[i.commsWave] = true; });
      return Object.keys(set).sort();
    },
    CARD_CODES() {
      return CARD_CODES;
    },
    FORCE_CODES() {
      return FORCE_CODES;
    },
    RELINK_CODES() {
      return RELINK_CODES;
    },
    segmentCounts() {
      const c = { MENSAL: 0, NAO_MENSAL: 0 };
      this.allItems.forEach((i) => { if (i.status !== "EXCLUIDA") c[this.segmentOf(i)]++; });
      return c;
    },
    confirmWord() {
      return this.segment === "NAO_MENSAL" ? "MIGRAR NAO MENSAL" : "MIGRAR";
    },
    tomorrowIso() {
      return this.isoDate(this.addDays(new Date(), 1));
    },
    firstChargeProblem() {
      const m = this.modal;
      if (m.type !== "card" || m.firstChargeMode !== "DATE") return "";
      if (!m.firstChargeDate) return "Escolha a data da 1ª cobrança.";
      if (m.firstChargeDate < this.tomorrowIso) return "A data precisa ser depois de hoje.";
      if (Number(m.firstChargeDate.slice(8, 10)) > 27) return "A data precisa ser até o dia 27 (corte do ASAAS).";
      return "";
    },
    staleLinkIds() {
      const ids = {};
      this.items.forEach((i) => {
        if (this.isStaleLink(i) || (i.status === "ERRO" && RELINK_CODES.indexOf(i.errorCode) !== -1)) ids[i.subscriptionId] = true;
      });
      return Object.keys(ids);
    },
    relinkFixable() {
      return this.modal.relink.filter((r) => r.canFix);
    },
    relinkManual() {
      return this.modal.relink.filter((r) => RELINK_MANUAL.indexOf(r.code) !== -1).length;
    },
    relinkText() {
      return this.modal.relink.map((r) => r.companyName + " (" + r.contextCode + ", " + r.subscriptionId + ")\n  Vinculada: " + (r.currentIuguSubscriptionId || "—") +
        "\n  Que cobra: " + (r.realIuguSubscriptionId || "—") + (r.evidenceInvoiceId ? " (boleto " + r.evidenceInvoiceId + ", venc. " + this.fmtDate(r.evidenceDueDate) + ")" : "") +
        "\n  " + r.code + ": " + r.message + (r.technicalDetail ? "\n  Detalhe técnico: " + r.technicalDetail : "")).join("\n\n");
    },
    migratedIds() {
      return this.items.filter((i) => i.status === "MIGRADA").map((i) => i.subscriptionId);
    },
    cardRowsAll() {
      const byId = {};
      this.items.forEach((i) => { byId[i.subscriptionId] = i; });
      return Object.keys(this.cards).map((k) => ({ card: this.cards[k], item: byId[k] || { subscriptionId: k } }));
    },
    cardBatches() {
      const set = {};
      this.cardRowsAll.forEach((x) => { if (x.card.batchName) set[x.card.batchName] = true; });
      return Object.keys(set).sort();
    },
    cardSegments() {
      const base = this.cardRowsAll.filter((x) => !this.cardPanel.batch || x.card.batchName === this.cardPanel.batch);
      return CARD_SEGMENTS.map((s) => ({ key: s.key, label: s.label, count: base.filter((x) => this.cardInSegment(x.card, s.key)).length }));
    },
    cardRows() {
      const q = (this.cardPanel.search || "").toLowerCase().trim();
      const digits = q.replace(/\D/g, "");
      return this.cardRowsAll
        .filter((x) => !this.cardPanel.batch || x.card.batchName === this.cardPanel.batch)
        .filter((x) => this.cardInSegment(x.card, this.cardPanel.segment))
        .filter((x) => !q || (x.item.companyName || "").toLowerCase().indexOf(q) !== -1 || (digits && (x.item.cnpj || "").replace(/\D/g, "").indexOf(digits) !== -1))
        .sort((a, b) => String(a.card.linkExpiresAt || "").localeCompare(String(b.card.linkExpiresAt || "")));
    },
    cardSelectedIds() {
      return Object.keys(this.cardPanel.selected).filter((k) => this.cardPanel.selected[k]);
    },
    cardAllSelected() {
      return this.cardRows.length > 0 && this.cardRows.every((x) => this.cardPanel.selected[x.card.subscriptionId]);
    },
    activeCardLinks() {
      return Object.keys(this.cards).map((k) => this.cards[k]).filter((c) => c.state === "ATIVO" && c.link);
    },
    reconcileFixed() {
      return this.modal.reconcile.filter((r) => r.fixed.length).length;
    },
    reconcileClean() {
      return this.modal.reconcile.filter((r) => !r.fixed.length && !r.problems.length).length;
    },
    reconcileProblems() {
      return this.modal.reconcile.filter((r) => r.problems.length).length;
    },
    reconcileText() {
      return this.modal.reconcile.map((r) => r.companyName + " (" + r.subscriptionId + ")\n  Corrigido: " + (r.fixed.join("; ") || "nada") + "\n  Precisa de ação: " + (r.problems.join("; ") || "nada") + (r.technicalDetail ? "\n  Detalhe técnico: " + r.technicalDetail : "")).join("\n\n");
    },
    serverCounts() {
      return (this.server.status && this.server.status.counts) || { queued: 0, dueNow: 0, waitingRetry: 0, processing: 0, paused: 0, awaitingCard: 0 };
    },
    serverWorker() {
      return (this.server.status && this.server.status.worker) || {};
    },
    serverBusyWork() {
      const c = this.serverCounts;
      return !!(this.serverWorker.running || c.queued || c.processing);
    },
    serverStateLabel() {
      if (this.serverWorker.running) return "rodando";
      const c = this.serverCounts;
      if (c.queued || c.processing) return "retomando...";
      if (c.paused) return "pausada";
      return "concluída";
    },
    serverProgress() {
      const w = this.serverWorker;
      const c = this.serverCounts;
      const done = (w.ok || 0) + (w.failed || 0);
      return this.pct(done, done + c.queued + c.processing);
    },
    historyGroups() {
      return HISTORY_GROUPS;
    },
    historyRows() {
      const group = HISTORY_GROUPS.find((g) => g.key === this.history.group) || HISTORY_GROUPS[0];
      const q = (this.history.search || "").toLowerCase().trim();
      return this.history.events.filter((e) => {
        if (group.types && group.types.indexOf(e.type) === -1) return false;
        if (!q) return true;
        return ((e.companyName || "") + " " + (e.message || "") + " " + this.eventLabel(e.type) + " " + (e.whoName || "")).toLowerCase().indexOf(q) !== -1;
      });
    },
    historyPage() {
      return this.historyRows.slice(0, this.history.limit);
    },
    stats() {
      const s = {
        scope: 0, migrated: 0, inProgress: 0, errors: 0, pending: 0, excluded: 0, critical: 0,
        mrrMigrated: 0, mrrToMigrate: 0, toMigrateSynced: 0, toMigrateUnsynced: 0,
        syncOk: 0, syncPending: 0, syncError: 0
      };
      this.items.forEach((i) => {
        if (i.status === "EXCLUIDA") { s.excluded++; return; }
        if (!this.inScope(i)) return;
        s.scope++;
        if (i.status === "MIGRADA") {
          s.migrated++;
          s.mrrMigrated += Number(i.asaasValue || i.mrrAfter || 0);
          return;
        }
        if (i.status === "NA_FILA" || i.status === "PROCESSANDO" || i.status === "REVERTENDO") s.inProgress++;
        else if (i.status === "ERRO") s.errors++;
        else s.pending++;
        if (this.isCritical(i)) s.critical++;
        if (i.iuguSyncStatus === "OK" && i.iuguMrr !== null && i.iuguMrr !== undefined) {
          s.toMigrateSynced++;
          s.mrrToMigrate += Number(i.iuguMrr);
          s.syncOk++;
        } else {
          s.toMigrateUnsynced++;
          if (i.iuguSyncStatus === "ERRO") s.syncError++;
          else s.syncPending++;
        }
      });
      return s;
    },
    segTitle() {
      const s = this.stats;
      return `Migradas ${s.migrated} · em andamento ${s.inProgress} · erro ${s.errors} · a migrar ${s.pending}`;
    },
    lastCohortAt() {
      let max = null;
      this.items.forEach((i) => {
        if (i.cohortCheckedAt && (!max || i.cohortCheckedAt > max)) max = i.cohortCheckedAt;
      });
      return max;
    },
    syncCandidates() {
      return this.items
        .filter((i) => this.inScope(i) && i.status !== "EXCLUIDA" && i.status !== "MIGRADA" && i.status !== "PROCESSANDO" && i.iuguSyncStatus === "PENDENTE")
        .map((i) => i.subscriptionId);
    },
    errorGroups() {
      const map = {};
      this.items.forEach((i) => {
        if (i.status !== "ERRO" || !this.inScope(i)) return;
        const code = i.errorCode || "INESPERADO";
        if (!map[code]) map[code] = { code: code, count: 0, critical: false };
        map[code].count++;
        if (this.isCritical(i)) map[code].critical = true;
      });
      return Object.keys(map).map((k) => map[k]).sort((a, b) => (b.critical - a.critical) || (b.count - a.count));
    },
    filteredRows() {
      const f = this.filters;
      const search = (f.search || "").toLowerCase().trim();
      const digits = search.replace(/\D/g, "");
      const rows = this.items.filter((r) => {
        if (!this.matchStatus(r, f.status)) return false;
        if (f.errorCode && (r.status !== "ERRO" || (r.errorCode || "INESPERADO") !== f.errorCode)) return false;
        if (search) {
          const hay = [(r.companyName || ""), (r.email || ""), (r.financialEmail || ""), r.subscriptionId, r.companyId, r.iuguSubscriptionId || ""].join(" ").toLowerCase();
          const cnpj = (r.cnpj || "").replace(/\D/g, "");
          if (hay.indexOf(search) === -1 && !(digits.length >= 4 && cnpj.indexOf(digits) !== -1)) return false;
        }
        const due = this.dateOnly(r.iuguExpiresAt);
        if (f.dueFrom && (!due || due < f.dueFrom)) return false;
        if (f.dueTo && (!due || due > f.dueTo)) return false;
        if (f.cycleInvoiced === "sim" && !r.iuguCycleInvoiced) return false;
        if (f.cycleInvoiced === "nao" && (r.iuguCycleInvoiced || r.iuguSyncStatus !== "OK")) return false;
        if (f.mrrDivergent && !this.hasDivergence(r)) return false;
        if (f.cappedDay && !(this.dayOf(r.iuguExpiresAt) > 27)) return false;
        if (f.noIugu && r.iuguSyncStatus === "OK") return false;
        if (f.staleLink && !this.isStaleLink(r) && !(r.status === "ERRO" && RELINK_CODES.indexOf(r.errorCode) !== -1)) return false;
        if (f.payKind === "CARTAO" && r.iuguPaymentKind !== "CARTAO") return false;
        if (f.payKind === "BOLETO" && r.iuguPaymentKind !== "BOLETO" && r.iuguPaymentKind !== "PIX") return false;
        if (f.payKind === "INDEFINIDO" && this.payKnown(r)) return false;
        if (f.payKind === "DIVERGE" && !this.payDiverges(r)) return false;
        if (f.payMonth && !this.paidInMonth(r, f.payMonth)) return false;
        if (f.wave === "__SEM__" && r.commsWave) return false;
        if (f.wave && f.wave !== "__SEM__" && r.commsWave !== f.wave) return false;
        return true;
      });
      const key = this.sort.key;
      const dir = this.sort.dir;
      return rows.sort((a, b) => {
        let va = a[key];
        let vb = b[key];
        if (va === null || va === undefined || va === "") return 1;
        if (vb === null || vb === undefined || vb === "") return -1;
        if (typeof va === "string") va = va.toLowerCase();
        if (typeof vb === "string") vb = vb.toLowerCase();
        if (va < vb) return -1 * dir;
        if (va > vb) return 1 * dir;
        return 0;
      });
    },
    windowStats() {
      const w = { total: 0, mrr: 0, invoiced: 0, unsynced: 0 };
      this.filteredRows.forEach((r) => {
        w.total++;
        w.mrr += Number(r.iuguMrr || 0);
        if (r.iuguCycleInvoiced) w.invoiced++;
      });
      w.unsynced = this.items.filter((i) => this.matchStatus(i, this.filters.status) && i.iuguSyncStatus !== "OK").length;
      return w;
    },
    pageCount() {
      return Math.max(1, Math.ceil(this.filteredRows.length / this.pageSize));
    },
    pagedRows() {
      const start = (this.page - 1) * this.pageSize;
      return this.filteredRows.slice(start, start + this.pageSize);
    },
    pageAllSelected() {
      return this.pagedRows.length > 0 && this.pagedRows.every((r) => this.selected[r.subscriptionId]);
    },
    selectedIds() {
      return Object.keys(this.selected);
    },
    selectedCount() {
      return this.selectedIds.length;
    },
    filteredSelectedCount() {
      return this.filteredRows.filter((r) => this.selected[r.subscriptionId]).length;
    },
    selectedOutsideFilter() {
      return this.selectedCount - this.filteredSelectedCount;
    },
    selectedRows() {
      return this.items.filter((i) => this.selected[i.subscriptionId]);
    },
    selectedMrr() {
      return this.selectedRows.reduce((sum, r) => sum + Number(this.bestMrr(r)), 0);
    },
    modalTitle() {
      const n = this.modal.ids.length;
      if (this.modal.type === "simulate") return `Simulação (${n})`;
      if (this.modal.type === "migrate") return `Migrar ${n} assinatura(s) para o ASAAS MAM`;
      if (this.modal.type === "exclude") return `Excluir ${n} da migração`;
      if (this.modal.type === "rollback") return `Reverter ${n} migração(ões)`;
      if (this.modal.type === "csv") return "Importar lista";
      if (this.modal.type === "paste") return "Colar lista";
      if (this.modal.type === "wave") return "Leva de comunicação";
      if (this.modal.type === "relink") return `Corrigir vínculo IUGU (${n})`;
      if (this.modal.type === "export") return "Exportar planilha";
      return "";
    },
    exportRows() {
      return this.modal.exportScope === "selecao" ? this.selectedRows : this.filteredRows;
    },
    exportRefreshCount() {
      return this.exportRows.filter((r) => r.status !== "MIGRADA" && r.status !== "PROCESSANDO" && r.status !== "REVERTENDO").length;
    },
    exportEstimate() {
      const seconds = Math.ceil(this.exportRefreshCount * 0.45);
      return seconds < 90 ? seconds + " s" : Math.ceil(seconds / 60) + " min";
    },
    modalMrr() {
      const set = {};
      this.modal.ids.forEach((id) => { set[id] = true; });
      return this.items.filter((i) => set[i.subscriptionId]).reduce((sum, r) => sum + Number(this.bestMrr(r)), 0);
    },
    simReady() {
      return this.modal.plans.filter((p) => p.canMigrate);
    },
    simBlocked() {
      return this.modal.plans.filter((p) => !p.canMigrate);
    },
    simWarn() {
      return this.simReady.filter((p) => p.warnings && p.warnings.length).length;
    },
    simMrr() {
      return this.simReady.reduce((sum, p) => sum + Number(p.asaasValue || 0), 0);
    },
    simBlockedGroups() {
      const map = {};
      this.simBlocked.forEach((p) => {
        const code = p.errorCode || "INESPERADO";
        map[code] = (map[code] || 0) + 1;
      });
      return Object.keys(map).map((k) => ({ code: k, count: map[k] }));
    }
  },
  watch: {
    filters: {
      deep: true,
      handler() { this.page = 1; this.lastClickedIndex = null; }
    },
    sort: {
      deep: true,
      handler() { this.lastClickedIndex = null; }
    },
    page() { this.lastClickedIndex = null; },
    pageSize() { this.page = 1; this.lastClickedIndex = null; },
    pageCount(count) { if (this.page > count) this.page = count; }
  },
  beforeRouteLeave(to, from, next) {
    if (this.runner.active && !this.runner.paused && !window.confirm("A migração está rodando. Sair agora interrompe o acompanhamento (o que está na fila continua lá e pode ser retomado). Sair mesmo assim?")) {
      next(false);
      return;
    }
    this.runner.active = false;
    next();
  },
  beforeMount() {
    try { const saved = window.localStorage.getItem("migIuguAsaasSegment"); if (saved === "NAO_MENSAL" || saved === "MENSAL") this.segment = saved; } catch (e) { }
    const info = window.localStorage.getItem("mamUserMasterInfo");
    if (!info || UTIL.checkP(JSON.parse(info).id) === false) {
      this.$router.push({ path: "/" });
      return;
    }
    let stored = "";
    try { stored = window.sessionStorage.getItem(PASSWORD_KEY) || ""; } catch (e) { stored = ""; }
    if (stored) {
      this.password = stored;
      this.load();
      this.loadHistory();
      this.pollServerQueue();
    }
    window.addEventListener("beforeunload", this.onBeforeUnload);
  },
  beforeDestroy() {
    window.removeEventListener("beforeunload", this.onBeforeUnload);
    if (this.server.timer) clearTimeout(this.server.timer);
    this.server.timer = null;
    this.server.destroyed = true;
    this.runner.active = false;
  },
  methods: {
    defaultFilters() {
      return {
        search: "", status: "ATIVOS", dueFrom: "", dueTo: "", errorCode: "",
        cycleInvoiced: "", mrrDivergent: false, cappedDay: false, noIugu: false, payKind: "", payMonth: "", wave: "", staleLink: false
      };
    },
    api(method, url, data) {
      return REQUEST_SERVICE.requestWithHeaders(method, url, data, { "X-Migration-Password": this.password })
        .catch((e) => {
          const status = e && e.response ? e.response.status : 0;
          if (e && (status === 0 || status >= 500 || status === 408 || status === 429))
            e.incidentId = this.recordIncident(e, method + " " + String(url || "").split("/api/").pop());
          if (status === 403) this.lockOut();
          if (status === 401 || !window.localStorage.getItem("marQAdminAuthUser")) this.sessionExpired();
          throw e;
        });
    },
    sessionExpired() {
      if (this.expiredNotified) return;
      this.expiredNotified = true;
      this.runner.active = false;
      swal("Sessão expirada", "Entre de novo no Master. O que está na fila continua lá e pode ser retomado.", "warning");
      this.$router.push({ path: "/login" });
    },
    unlock() {
      if (!this.passwordInput) return;
      this.authChecking = true;
      this.authError = "";
      REQUEST_SERVICE.requestWithHeaders("POST", MIGRACAO_IUGU_ASAAS_ACESSO, {}, { "X-Migration-Password": this.passwordInput })
        .then(() => {
          this.password = this.passwordInput;
          this.passwordInput = "";
          try { window.sessionStorage.setItem(PASSWORD_KEY, this.password); } catch (e) { }
          this.load();
          this.loadHistory();
          this.pollServerQueue();
        })
        .catch((e) => {
          const data = e && e.response ? e.response.data : null;
          this.authError = typeof data === "string" && data ? data : (e && e.response && e.response.status === 403 ? "Senha incorreta." : "Não foi possível validar agora.");
        })
        .finally(() => { this.authChecking = false; });
    },
    lockOut() {
      this.password = "";
      this.runner.active = false;
      try { window.sessionStorage.removeItem(PASSWORD_KEY); } catch (e) { }
    },
    onBeforeUnload(e) {
      if (this.runner.active && !this.runner.paused) {
        e.preventDefault();
        e.returnValue = "";
      }
    },
    loadCards() {
      return this.api("GET", MIGRACAO_IUGU_ASAAS_CARTOES)
        .then((r) => {
          const map = {};
          (r.data || []).forEach((c) => { map[c.subscriptionId] = c; });
          this.cards = map;
          this.cardsError = "";
        })
        .catch((e) => {
          const status = e && e.response ? e.response.status : 0;
          if (status === 409) this.cardsError = typeof e.response.data === "string" ? e.response.data : "Tabela de links de cartão ausente: rode o script 02_IuguAsaasMigrationCard.sql.";
        });
    },
    cardOf(r) {
      return r ? this.cards[r.subscriptionId] || null : null;
    },
    cardSummary(c) {
      if (!c) return "";
      if (c.state === "ATIVO" && c.linkExpiresAt) return "link de cartão até " + this.fmtDateTime(c.linkExpiresAt);
      return CARD_STATES[c.state] || c.state;
    },
    cardClass(c) {
      if (!c) return "";
      if (c.state === "CADASTRADO") return "txt-ok";
      if (c.state === "EXPIRADO" || c.state === "FALHOU") return "txt-danger";
      return "txt-warn";
    },
    canRequestCard(r) {
      if (!r || r.status === "MIGRADA" || r.status === "PROCESSANDO" || r.status === "NA_FILA" || r.status === "EXCLUIDA" || r.status === "REVERTENDO") return false;
      return !r.step || r.step === "CUSTOMER";
    },
    idsWithError(code) {
      return this.items.filter((i) => i.status === "ERRO" && this.inScope(i) && (i.errorCode || "INESPERADO") === code).map((i) => i.subscriptionId);
    },
    cardMessage(r) {
      const c = this.cardOf(r) || {};
      return "Olá! A cobrança da MarQ Ponto da " + r.companyName + " passa a ser feita pela plataforma ASAAS. " +
        "Para continuar pagando no cartão de crédito, cadastre o cartão neste link (válido até " + this.fmtDateTime(c.linkExpiresAt) + "): " + c.link + " . " +
        "Valor mensal: " + this.money(c.value) + ". A primeira cobrança no cartão será em " + this.fmtDate(c.nextDueDate) + ". Qualquer dúvida, estamos à disposição.";
    },
    openCardLinks(ids) {
      const eligible = (ids || []).filter((id) => {
        const r = this.items.find((i) => i.subscriptionId === id);
        return this.canRequestCard(r);
      });
      if (!eligible.length) {
        swal("Nada a fazer", "Nenhuma das selecionadas pode receber link de cartão (migradas, na fila ou com assinatura ASAAS já criada ficam de fora).", "info");
        return;
      }
      this.modal = emptyModal();
      this.modal.type = "card";
      this.modal.ids = eligible;
      const now = new Date();
      const pad = (n) => (n < 10 ? "0" : "") + n;
      this.modal.batch = "Lote " + pad(now.getDate()) + "/" + pad(now.getMonth() + 1) + " " + pad(now.getHours()) + ":" + pad(now.getMinutes());
    },
    cardInSegment(c, key) {
      if (key === "TODOS") return true;
      if (key === "GERANDO") return c.state === "SOLICITADO";
      if (key === "PRONTOS") return c.state === "ATIVO" && !c.sentAt;
      if (key === "ENVIADOS") return c.state === "ATIVO" && !!c.sentAt;
      if (key === "VENCENDO") {
        if (c.state !== "ATIVO" || !c.linkExpiresAt) return false;
        const left = new Date(c.linkExpiresAt).getTime() - Date.now();
        return left > 0 && left <= CARD_EXPIRING_HOURS * 3600000;
      }
      if (key === "EXPIRADOS") return c.state === "EXPIRADO";
      if (key === "FALHARAM") return c.state === "FALHOU";
      if (key === "CADASTRADOS") return c.state === "CADASTRADO";
      return false;
    },
    cardStageLabel(c) {
      if (c.state === "ATIVO") return c.sentAt ? "enviado, aguardando cadastro" : "pronto para enviar";
      return CARD_STATES[c.state] || c.state;
    },
    cardTimeLeft(c) {
      if (c.state !== "ATIVO" || !c.linkExpiresAt) return c.linkExpiresAt ? "até " + this.fmtDateTime(c.linkExpiresAt) : "—";
      const left = new Date(c.linkExpiresAt).getTime() - Date.now();
      if (left <= 0) return "vencido (conferindo)";
      const h = Math.floor(left / 3600000);
      const m = Math.floor((left % 3600000) / 60000);
      return "vence em " + (h ? h + "h " : "") + m + "min";
    },
    toggleCard(id) {
      this.$set(this.cardPanel.selected, id, !this.cardPanel.selected[id]);
    },
    toggleCardAll() {
      const all = this.cardAllSelected;
      const next = {};
      if (!all) this.cardRows.forEach((x) => { next[x.card.subscriptionId] = true; });
      this.cardPanel.selected = next;
    },
    markCardsSent(ids, sent) {
      if (!ids || !ids.length) return;
      this.cardPanel.busy = true;
      this.api("POST", MIGRACAO_IUGU_ASAAS_CARTAO_ENVIADO, { subscriptionIds: ids, sent: sent })
        .then((res) => {
          const affected = (res.data && res.data.affected) || 0;
          if (affected < ids.length)
            swal("Atenção", affected + " de " + ids.length + " marcados. Só links ativos podem ser marcados como enviados.", "info");
          this.cardPanel.selected = {};
          return this.loadCards();
        })
        .catch((e) => {
          const status = e && e.response ? e.response.status : 0;
          swal("Aviso", status === 409 && typeof e.response.data === "string" ? e.response.data : "Não consegui marcar o envio" + this.withDetail(e) + ".", "error");
        })
        .finally(() => { this.cardPanel.busy = false; });
    },
    payKnown(r) {
      return !!(r && r.iuguPaymentKind && r.iuguPaymentKind !== "INDEFINIDO");
    },
    payLabel(kind) {
      return PAY_LABEL[kind] || "não identificado";
    },
    payTagClass(kind) {
      return kind === "CARTAO" ? "tag-warn" : "tag-info";
    },
    payDiverges(r) {
      if (!this.payKnown(r)) return false;
      const iuguCard = r.iuguPaymentKind === "CARTAO";
      return iuguCard !== (r.paymentMethod === 2);
    },
    payTitle(r) {
      if (!r) return "";
      const parts = [];
      if (r.iuguPaidWith) parts.push("última fatura paga" + (r.iuguPaidAt ? " em " + this.fmtDate(r.iuguPaidAt) : "") + " via " + r.iuguPaidWith);
      if (r.iuguPayableWith) parts.push("assinatura aceita " + r.iuguPayableWith);
      return (r.iuguPaymentKind ? this.payLabel(r.iuguPaymentKind) : "") + (parts.length ? " (" + parts.join("; ") + ")" : "");
    },
    confirmCardLinks() {
      this.modal.running = true;
      const mode = this.modal.firstChargeMode || "AUTO";
      this.api("POST", MIGRACAO_IUGU_ASAAS_CARTAO_LINK, {
        subscriptionIds: this.modal.ids, batch: this.modal.batch, segment: this.segment,
        firstChargeMode: mode, firstChargeDate: mode === "DATE" ? this.modal.firstChargeDate : null
      })
        .then((res) => {
          const affected = (res.data && res.data.affected) || 0;
          this.closeModal();
          swal("Pedido enviado", affected + " link(s) de cartão em geração na fila do servidor. Em instantes eles aparecem na listagem e no painel da empresa.", "success");
          this.openServerQueue();
          return this.load();
        })
        .catch((e) => {
          this.modal.running = false;
          const status = e && e.response ? e.response.status : 0;
          const msg = status === 409 && typeof e.response.data === "string" ? e.response.data : "Não consegui pedir os links" + this.withDetail(e) + ".";
          swal("Aviso", msg, "error");
        });
    },
    exportCardLinks(ids) {
      const byId = {};
      this.items.forEach((i) => { byId[i.subscriptionId] = i; });
      const wanted = ids && ids.length ? ids.map((id) => this.cards[id]).filter((c) => c) : this.activeCardLinks;
      const header = ["Empresa", "CNPJ", "E-mail", "E-mail financeiro", "Telefone", "Lote", "Etapa", "Link de cadastro do cartão", "Válido até", "Enviado em", "Enviado por", "Valor mensal", "1ª cobrança no cartão", "Mensagem sugerida"];
      const lines = wanted.map((c) => {
        const r = byId[c.subscriptionId] || { companyName: c.subscriptionId };
        return [r.companyName, r.cnpj || "", r.email || "", r.financialEmail || "", r.phone || "", c.batchName || "", this.cardStageLabel(c), c.link || "", this.fmtDateTime(c.linkExpiresAt),
          c.sentAt ? this.fmtDateTime(c.sentAt) : "", c.sentBy || "",
          c.value === null || c.value === undefined ? "" : Number(c.value), this.fmtDate(c.nextDueDate), c.state === "ATIVO" ? this.cardMessage(r) : ""];
      });
      if (window.downloadListExcel) window.downloadListExcel(header, lines, "links-cartao-migracao-asaas");
      else this.downloadCsv(header, lines, "links-cartao-migracao-asaas");
    },
    segmentOf(r) {
      return r && r.planType !== null && r.planType !== undefined && r.planType !== 1 ? "NAO_MENSAL" : "MENSAL";
    },
    isNonMonthly(r) {
      return this.segmentOf(r) === "NAO_MENSAL";
    },
    cycleLabel(months) {
      return CYCLE_LABEL[months] || ("a cada " + months + " meses");
    },
    planTypeLabel(planType) {
      return PLAN_TYPE_LABEL[planType] || "sem periodicidade";
    },
    setSegment(segment) {
      if (this.segment === segment) return;
      this.segment = segment;
      this.applySegment();
      this.clearSelection();
      this.filters.errorCode = "";
      this.page = 1;
      try { window.localStorage.setItem("migIuguAsaasSegment", segment); } catch (e) { }
    },
    applySegment() {
      this.items = this.allItems.filter((i) => this.segmentOf(i) === this.segment);
    },
    isStaleLink(r) {
      if (!r || r.status === "MIGRADA" || r.status === "EXCLUIDA" || r.iuguSyncStatus !== "OK" || !r.localLastBoletoDueDate) return false;
      const at = (v) => v ? new Date(this.dateKey(v) + "T00:00:00").getTime() : null;
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const master = at(r.localLastBoletoDueDate);
      if (master < today - 45 * DAY_MS) return false;
      const last = at(r.iuguLastInvoiceDueDate);
      if (last !== null && last >= master - 25 * DAY_MS) return false;
      const expires = at(r.iuguExpiresAt);
      const stopped = r.iuguSuspended === true || r.iuguActive === false;
      return stopped || !(expires !== null && expires > master);
    },
    showStaleLinks() {
      this.filters = this.defaultFilters();
      this.filters.status = "TODOS";
      this.filters.staleLink = true;
    },
    statusLabelOf(status) {
      return STATUS_LABEL[status] || status;
    },
    relinkCount(code) {
      return this.modal.relink.filter((r) => r.code === code).length;
    },
    relinkClass(r) {
      if (r.code === "CORRIGIDA" || r.canFix) return "st-ok";
      if (r.code === "JA_CORRETO" || r.code === "MIGRADA_IUGU_REAL_PARADA") return "";
      if (r.code === "MIGRADA_IUGU_REAL_ATIVA") return "st-err";
      return "st-warn";
    },
    openRelink(ids) {
      const list = (ids || []).filter((id, i, all) => all.indexOf(id) === i);
      if (!list.length) return;
      this.modal = emptyModal();
      this.modal.type = "relink";
      this.modal.ids = list;
      this.modal.relinkPhase = "analyze";
      this.modal.relinkTotal = list.length;
      this.modal.queue = list.slice();
      this.modal.running = true;
      this.relinkStep(this.modal.id, MIGRACAO_IUGU_ASAAS_VINCULO_ANALISAR);
    },
    confirmRelink() {
      const m = this.modal;
      const fixable = this.relinkFixable.map((r) => r.subscriptionId);
      if (!fixable.length || m.confirmText !== "CORRIGIR") return;
      const keep = m.relink.filter((r) => !r.canFix);
      m.relink = keep;
      m.relinkPhase = "apply";
      m.relinkTotal = fixable.length;
      m.done = 0;
      m.queue = fixable.slice();
      m.running = true;
      this.relinkStep(m.id, MIGRACAO_IUGU_ASAAS_VINCULO_CORRIGIR);
    },
    relinkStep(modalId, url) {
      const m = this.modal;
      if (m.id !== modalId || m.type !== "relink") return;
      const chunk = m.queue.splice(0, RELINK_CHUNK);
      if (!chunk.length) {
        m.running = false;
        if (m.relinkPhase === "apply") {
          m.relinkPhase = "done";
          this.load();
          if (this.history.loaded) this.loadHistory();
        }
        return;
      }
      this.api("POST", url, { subscriptionIds: chunk })
        .then((res) => {
          if (this.modal.id !== modalId) return;
          m.httpFailures = 0;
          m.retryMessage = "";
          const rows = res.data || [];
          m.relink = m.relink.concat(rows);
          m.done += rows.length || chunk.length;
          this.relinkStep(modalId, url);
        })
        .catch((e) => {
          if (this.modal.id !== modalId) return;
          m.queue = chunk.concat(m.queue);
          const status = e && e.response ? e.response.status : 0;
          if (this.isTransientHttp(status) && m.httpFailures < 20) {
            m.httpFailures++;
            const wait = this.retryWait(m.httpFailures);
            m.retryMessage = `A API respondeu ${status || "sem resposta"}${this.withDetail(e)}. Tentando de novo sozinho em ${wait}s.`;
            setTimeout(() => this.relinkStep(modalId, url), wait * 1000);
            return;
          }
          m.running = false;
          if (m.relinkPhase === "apply") {
            m.relinkPhase = "done";
            this.load();
          }
          swal("Aviso", `Parou em ${m.done} de ${m.relinkTotal}${this.withDetail(e)}.`, "error");
        });
    },
    openReconcile(ids) {
      const list = (ids || []).filter((id) => {
        const r = this.items.find((i) => i.subscriptionId === id);
        return r && r.status === "MIGRADA";
      });
      if (!list.length) {
        swal("Nada a reconciliar", "Reconciliar vale só para assinaturas já migradas.", "info");
        return;
      }
      this.modal = emptyModal();
      this.modal.type = "reconcile";
      this.modal.ids = list;
      this.modal.queue = list.slice();
      this.modal.running = true;
      this.reconcileStep(this.modal.id);
    },
    reconcileStep(modalId) {
      const m = this.modal;
      if (m.id !== modalId || m.type !== "reconcile") return;
      const chunk = m.queue.splice(0, RECONCILE_CHUNK);
      if (!chunk.length) {
        m.running = false;
        this.load();
        if (this.history.loaded) this.loadHistory();
        return;
      }
      this.api("POST", MIGRACAO_IUGU_ASAAS_RECONCILIAR, { subscriptionIds: chunk })
        .then((res) => {
          if (this.modal.id !== modalId) return;
          m.httpFailures = 0;
          m.retryMessage = "";
          m.reconcile = m.reconcile.concat(res.data || []);
          m.done = m.reconcile.length;
          this.reconcileStep(modalId);
        })
        .catch((e) => {
          if (this.modal.id !== modalId) return;
          m.queue = chunk.concat(m.queue);
          const status = e && e.response ? e.response.status : 0;
          if (this.isTransientHttp(status) && m.httpFailures < 20) {
            m.httpFailures++;
            const wait = this.retryWait(m.httpFailures);
            m.retryMessage = `A API respondeu ${status || "sem resposta"}${this.withDetail(e)}. Tentando de novo sozinho em ${wait}s.`;
            setTimeout(() => this.reconcileStep(modalId), wait * 1000);
            return;
          }
          m.running = false;
          swal("Aviso", `A reconciliação parou em ${m.reconcile.length} de ${m.ids.length}${this.withDetail(e)}.`, "error");
        });
    },
    recordIncident(e, context) {
      const text = this.buildLaudo(e, context);
      const status = e && e.response ? e.response.status : 0;
      incidentSeq++;
      this.laudos.unshift({ id: incidentSeq, time: new Date().toLocaleTimeString("pt-BR"), title: (context || "chamada à API") + " — " + (status || "sem resposta"), text: text });
      if (this.laudos.length > 30) this.laudos.pop();
      return incidentSeq;
    },
    buildLaudo(e, context) {
      const res = e && e.response;
      const cfg = (e && e.config) || (res && res.config) || {};
      const lines = ["LAUDO TÉCNICO — Migração IUGU → ASAAS (Master)", "Quando: " + new Date().toLocaleString("pt-BR"), "Ação: " + (context || "—"),
        "Chamada: " + String(cfg.method || "").toUpperCase() + " " + (cfg.url || "—"),
        "Status HTTP: " + (res ? res.status + " " + (res.statusText || "") : "sem resposta (rede, tempo esgotado ou servidor caiu)")];
      if (cfg.data) lines.push("Enviado: " + String(typeof cfg.data === "string" ? cfg.data : JSON.stringify(cfg.data)).slice(0, 2000));
      const headers = (res && res.headers) || {};
      ["request-id", "request-context", "x-ms-request-id", "x-arr-log-id", "date"].forEach((h) => { if (headers[h]) lines.push("Cabeçalho " + h + ": " + headers[h]); });
      const d = res ? res.data : null;
      if (d && typeof d === "object") {
        if (d.Message || d.message) lines.push("Mensagem: " + (d.Message || d.message));
        if (d.ExceptionType || d.ExceptionMessage) lines.push("Exceção: " + (d.ExceptionType || "") + " — " + (d.ExceptionMessage || ""));
        if (d.StackTrace) lines.push("Pilha:\n" + d.StackTrace);
        if (d.InnerException) lines.push("Exceção interna: " + JSON.stringify(d.InnerException).slice(0, 4000));
        if (!d.Message && !d.message && !d.ExceptionMessage) lines.push("Resposta: " + JSON.stringify(d).slice(0, 4000));
      } else if (d) {
        lines.push("Resposta: " + String(d).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim().slice(0, 4000));
      } else if (e && e.message) {
        lines.push("Erro no navegador: " + e.message);
      }
      lines.push("Fila no servidor: " + JSON.stringify(this.serverCounts) + " · worker " + JSON.stringify({
        running: this.serverWorker.running, instance: this.serverWorker.instance, lastError: this.serverWorker.lastError, lastBeatAt: this.serverWorker.lastBeatAt }));
      lines.push("Página: " + window.location.href);
      lines.push("Navegador: " + (window.navigator ? window.navigator.userAgent : ""));
      return lines.join("\n");
    },
    openIncident(id) {
      const l = this.laudos.find((x) => x.id === id);
      if (!l) return;
      this.modal = emptyModal();
      this.modal.type = "laudo";
      this.modal.laudo = l.text;
    },
    openIncidentList() {
      this.modal = emptyModal();
      this.modal.type = "laudos";
    },
    openWorkerLaudo() {
      const w = this.serverWorker;
      this.modal = emptyModal();
      this.modal.type = "laudo";
      this.modal.laudo = ["LAUDO TÉCNICO — Fila da migração IUGU → ASAAS (servidor)", "Quando: " + new Date().toLocaleString("pt-BR"),
        "Aviso: " + (w.lastError || "—") + " (em " + this.fmtDateTime(w.lastErrorAt) + ")",
        "Instância: " + (w.instance || "—") + " · rodando: " + (w.running ? "sim" : "não") + " · desde " + this.fmtDateTime(w.startedAt) + " por " + (w.startedBy || "—"),
        "Última batida: " + this.fmtDateTime(w.lastBeatAt) + " · processadas " + (w.processed || 0) + " · migradas " + (w.ok || 0) + " · erros " + (w.failed || 0),
        "Contagens: " + JSON.stringify(this.serverCounts),
        "Detalhe técnico:\n" + (w.lastErrorDetail || "—")].join("\n");
    },
    copyItemLaudo(item) {
      const card = this.cardOf(item);
      const pick = {};
      ["subscriptionId", "companyId", "contextCode", "companyName", "cnpj", "status", "step", "errorCode", "errorMessage", "paymentMethod", "mrrMaster",
        "iuguSubscriptionId", "iuguSyncStatus", "iuguSyncError", "iuguMrr", "iuguExpiresAt", "iuguSuspended", "iuguLastInvoiceDueDate", "iuguLastInvoiceStatus",
        "asaasCustomerId", "asaasSubscriptionId", "asaasValue", "asaasNextDueDate", "asaasFirstPaymentUrl", "mrrBefore", "mrrAfter", "attempts", "nextAttemptAt",
        "queuedBy", "migratedAt", "migratedBy", "updatedAt"].forEach((k) => { pick[k] = item[k]; });
      const events = (this.drawer.events || []).map((e) => this.fmtDateTime(e.createdAt) + " [" + e.type + "] " + (e.whoName || "") + ": " + e.message + (e.dataJson ? "\n    dados: " + e.dataJson : ""));
      const text = ["LAUDO DA EMPRESA — Migração IUGU → ASAAS", "Gerado em: " + new Date().toLocaleString("pt-BR"),
        "Item: " + JSON.stringify(pick, null, 2), "Link de cartão: " + (card ? JSON.stringify(card, null, 2) : "—"),
        "Histórico (mais recente primeiro):", events.join("\n") || "—"].join("\n");
      this.copyText(text, "Laudo da empresa copiado");
    },
    copyText(text, okMessage) {
      const done = () => swal(okMessage || "Copiado", "", "success");
      const fallback = () => {
        const ta = document.createElement("textarea");
        ta.value = text;
        document.body.appendChild(ta);
        ta.select();
        try { document.execCommand("copy"); done(); } catch (err) { swal("Copie manualmente", text, "info"); }
        document.body.removeChild(ta);
      };
      if (window.navigator && window.navigator.clipboard && window.navigator.clipboard.writeText) window.navigator.clipboard.writeText(text).then(done, fallback);
      else fallback();
    },
    load() {
      this.loadCards();
      this.loading = !this.items.length;
      const seq = ++loadSeq;
      return this.api("GET", MIGRACAO_IUGU_ASAAS_ITENS)
        .then((r) => {
          if (seq !== loadSeq) return;
          const local = {};
          this.items.forEach((i) => { local[i.subscriptionId] = i; });
          const payload = r.data || {};
          this.shardsFailed = payload.shardsFailed || [];
          this.itemWarnings = payload.warnings || [];
          this.allItems.forEach((i) => { if (!local[i.subscriptionId]) local[i.subscriptionId] = i; });
          this.allItems = (payload.items || []).map((fresh) => {
            const mine = local[fresh.subscriptionId];
            return mine && mine.updatedAt && fresh.updatedAt && mine.updatedAt > fresh.updatedAt ? mine : fresh;
          });
          this.applySegment();
          this.pruneSelection();
          if (this.drawer.item) {
            const current = this.items.find((i) => i.subscriptionId === this.drawer.item.subscriptionId);
            if (current) this.drawer.item = current;
          }
        })
        .catch(() => swal("Aviso", "Falha ao carregar a base da migração.", "error"))
        .finally(() => { this.loading = false; });
    },
    mergeItems(updated) {
      if (!updated || !updated.length) return;
      const pos = {};
      const allPos = {};
      this.items.forEach((i, idx) => { pos[i.subscriptionId] = idx; });
      this.allItems.forEach((i, idx) => { allPos[i.subscriptionId] = idx; });
      updated.forEach((u) => {
        const allIdx = allPos[u.subscriptionId];
        if (allIdx === undefined) this.allItems.push(u);
        else this.$set(this.allItems, allIdx, u);
        const idx = pos[u.subscriptionId];
        if (this.segmentOf(u) !== this.segment) return;
        if (idx === undefined) this.items.push(u);
        else this.$set(this.items, idx, u);
        if (this.drawer.item && this.drawer.item.subscriptionId === u.subscriptionId) this.drawer.item = u;
      });
    },
    refreshCohort() {
      this.refreshing = true;
      this.api("POST", MIGRACAO_IUGU_ASAAS_COORTE, {})
        .then((r) => {
          this.refreshResult = r.data;
          const d = r.data || {};
          const failed = d.shardsFailed && d.shardsFailed.length;
          swal(failed ? "Atualizado com pendências" : "Base atualizada",
            `${d.found} assinatura(s) no critério · ${d.inserted} nova(s) · ${d.updated} atualizada(s) · ${d.leftCohort} saíram do critério` +
            (failed ? `\n\nBancos sem resposta: ${d.shardsFailed.length} (itens deles não foram alterados).` : ""),
            failed ? "warning" : "success");
          return this.load();
        })
        .catch(() => swal("Aviso", "Falha ao atualizar a base.", "error"))
        .finally(() => { this.refreshing = false; });
    },
    inScope(i) {
      return i.inCohort || !!i.step || i.status === "MIGRADA";
    },
    matchStatus(r, status) {
      if (status === "TODOS") return true;
      if (status === "EXCLUIDA") return r.status === "EXCLUIDA";
      if (status === "FORA") return !this.inScope(r) && r.status !== "EXCLUIDA";
      if (!this.inScope(r)) return false;
      if (status === "ATIVOS") return r.status !== "EXCLUIDA";
      if (status === "NA_FILA") return r.status === "NA_FILA" || r.status === "PROCESSANDO" || r.status === "REVERTENDO";
      if (status === "PENDENTE") return r.status === "PENDENTE" || r.status === "REVERTIDA";
      return r.status === status;
    },
    isCritical(r) {
      if (r.status === "ERRO" && CRITICAL_CODES.indexOf(r.errorCode) !== -1) return true;
      return (r.status === "ERRO" || r.status === "NA_FILA") && (r.step === "ASSINATURA" || r.step === "BANCO");
    },
    hasDivergence(r) {
      return !this.isNonMonthly(r) && r.iuguMrr !== null && r.iuguMrr !== undefined && Math.abs(Number(r.iuguMrr) - Number(r.mrrMaster)) >= 0.01;
    },
    bestMrr(r) {
      if (this.isNonMonthly(r)) {
        const cycle = r.iuguCycleMonths || 1;
        const value = r.status === "MIGRADA" ? (r.asaasValue || 0) : (r.iuguMrr !== null && r.iuguMrr !== undefined ? r.iuguMrr : 0);
        return Number(value) / cycle;
      }
      if (r.status === "MIGRADA") return r.asaasValue || r.mrrAfter || 0;
      return r.iuguMrr !== null && r.iuguMrr !== undefined ? r.iuguMrr : r.mrrMaster || 0;
    },
    quickStatus(status) {
      this.filters.status = status;
      this.filters.errorCode = "";
    },
    quickCritical() {
      this.filters.status = "TODOS";
      this.filters.errorCode = "";
      this.filters.search = "";
      this.sort = { key: "status", dir: 1 };
      const critical = {};
      this.items.filter(this.isCritical).forEach((i) => { critical[i.subscriptionId] = true; });
      this.selected = critical;
    },
    toggleErrorFilter(code) {
      if (this.filters.errorCode === code) {
        this.filters.errorCode = "";
        return;
      }
      this.filters.status = "ERRO";
      this.filters.errorCode = code;
    },
    clearFilters() {
      this.filters = this.defaultFilters();
    },
    presetDays(fromOffset, toOffset) {
      this.filters.dueFrom = this.isoDate(this.addDays(new Date(), fromOffset));
      this.filters.dueTo = this.isoDate(this.addDays(new Date(), toOffset));
    },
    presetToMonthEnd(fromOffset) {
      const today = new Date();
      this.filters.dueFrom = this.isoDate(this.addDays(today, fromOffset));
      this.filters.dueTo = this.isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 0));
    },
    dateKey(v) {
      return v ? String(v).slice(0, 10) : "";
    },
    paidInMonth(r, monthKey) {
      if (r.iuguPaidDueDate) return String(r.iuguPaidDueDate).slice(0, 7) === monthKey;
      const paid = ["paid", "partially_paid", "externally_paid"].indexOf(r.iuguLastInvoiceStatus) !== -1;
      return paid && !!r.iuguLastInvoiceDueDate && String(r.iuguLastInvoiceDueDate).slice(0, 7) === monthKey;
    },
    suggestedWave() {
      if (!this.filters.payMonth) return "";
      const parts = this.filters.payMonth.split("-");
      return "Fatura " + parts[1] + "/" + parts[0] + " paga na IUGU";
    },
    openWave(ids) {
      if (!ids || !ids.length) return;
      this.modal = emptyModal();
      this.modal.type = "wave";
      this.modal.ids = ids.slice();
      this.modal.wave = this.filters.wave && this.filters.wave !== "__SEM__" ? this.filters.wave : this.suggestedWave();
    },
    confirmWave(clear) {
      this.modal.running = true;
      const wave = clear ? "" : this.modal.wave.trim();
      this.api("POST", MIGRACAO_IUGU_ASAAS_LEVA, { subscriptionIds: this.modal.ids, batch: wave })
        .then((res) => {
          const affected = (res.data && res.data.affected) || 0;
          this.closeModal();
          swal(clear ? "Removidas da leva" : "Leva definida", affected + " empresa(s) " + (clear ? "sem leva agora." : "na leva \"" + wave + "\"."), "success");
          return this.load();
        })
        .catch((e) => {
          this.modal.running = false;
          const status = e && e.response ? e.response.status : 0;
          swal("Aviso", status === 409 && typeof e.response.data === "string" ? e.response.data : "Não consegui definir a leva" + this.withDetail(e) + ".", "error");
        });
    },
    presetNextMonth() {
      const today = new Date();
      this.filters.dueFrom = this.isoDate(new Date(today.getFullYear(), today.getMonth() + 1, 1));
      this.filters.dueTo = this.isoDate(new Date(today.getFullYear(), today.getMonth() + 2, 0));
    },
    sortBy(key) {
      if (this.sort.key === key) this.sort.dir = -this.sort.dir;
      else this.sort = { key: key, dir: 1 };
    },
    sortMark(key) {
      if (this.sort.key !== key) return "";
      return this.sort.dir === 1 ? "▲" : "▼";
    },
    isSelected(r) {
      return !!this.selected[r.subscriptionId];
    },
    toggleRow(r, idx, event) {
      const next = Object.assign({}, this.selected);
      const willSelect = !next[r.subscriptionId];
      const last = this.lastClickedIndex;
      if (event && event.shiftKey && last !== null && last < this.pagedRows.length) {
        const from = Math.min(last, idx);
        const to = Math.min(Math.max(last, idx), this.pagedRows.length - 1);
        for (let i = from; i <= to; i++) {
          const id = this.pagedRows[i].subscriptionId;
          if (willSelect) next[id] = true;
          else delete next[id];
        }
      } else if (willSelect) {
        next[r.subscriptionId] = true;
      } else {
        delete next[r.subscriptionId];
      }
      this.lastClickedIndex = idx;
      this.selected = next;
    },
    togglePage() {
      const next = Object.assign({}, this.selected);
      const select = !this.pageAllSelected;
      this.pagedRows.forEach((r) => {
        if (select) next[r.subscriptionId] = true;
        else delete next[r.subscriptionId];
      });
      this.selected = next;
    },
    selectAllFiltered() {
      const next = Object.assign({}, this.selected);
      this.filteredRows.forEach((r) => { next[r.subscriptionId] = true; });
      this.selected = next;
    },
    selectIds(ids) {
      const next = {};
      ids.forEach((id) => { next[id] = true; });
      this.selected = next;
    },
    keepOnlyFiltered() {
      const next = {};
      this.filteredRows.forEach((r) => { if (this.selected[r.subscriptionId]) next[r.subscriptionId] = true; });
      this.selected = next;
    },
    clearSelection() {
      this.selected = {};
      this.lastClickedIndex = null;
    },
    pruneSelection() {
      const exists = {};
      this.items.forEach((i) => { exists[i.subscriptionId] = true; });
      const next = {};
      Object.keys(this.selected).forEach((id) => { if (exists[id]) next[id] = true; });
      this.selected = next;
    },

    startSync(ids, onDone) {
      if (!ids || !ids.length || this.runner.active) return;
      const byId = {};
      this.items.forEach((i) => { byId[i.subscriptionId] = i; });
      const eligible = ids.filter((id) => byId[id] && byId[id].status !== "MIGRADA" && byId[id].status !== "PROCESSANDO" && byId[id].status !== "REVERTENDO");
      if (!eligible.length) {
        if (onDone) onDone();
        else swal("Nada a ler", "As selecionadas já estão migradas ou em processamento.", "info");
        return;
      }
      this.runner = emptyRunner();
      this.runner.onDone = onDone || null;
      this.runner.active = true;
      this.runner.mode = "sync";
      this.runner.queue = eligible.slice();
      this.runner.total = eligible.length;
      this.log("info", `Leitura da IUGU iniciada para ${eligible.length} assinatura(s)` + (ids.length > eligible.length ? ` (${ids.length - eligible.length} migradas/em processamento ignoradas).` : "."));
      this.syncStep(this.runner.id);
    },
    syncStep(runId) {
      const r = this.runner;
      if (r.id !== runId || !r.active || r.paused || r.inFlight) return;
      if (!r.queue.length) {
        this.finishRunner(`Leitura concluída: ${r.ok} ok, ${r.failed} com erro` + (r.skipped ? `, ${r.skipped} pulada(s) (em uso por outra sessão).` : "."));
        return;
      }
      const chunk = r.queue.splice(0, SYNC_CHUNK);
      r.inFlight = true;
      this.api("POST", MIGRACAO_IUGU_ASAAS_SYNC_IUGU, { subscriptionIds: chunk, max: SYNC_CHUNK })
        .then((res) => {
          if (this.runner.id !== runId) return;
          r.inFlight = false;
          r.httpFailures = 0;
          const d = res.data || {};
          this.mergeItems(d.items);
          r.throttleMs = d.throttleIntervalMs;
          let transient = 0;
          (d.items || []).forEach((it) => {
            if (it.iuguSyncStatus === "OK" && !it.iuguSyncError) { r.ok++; r.done++; return; }
            if (it.iuguSyncStatus === "ERRO") { r.failed++; r.done++; this.log("err", `${it.companyName}: ${it.iuguSyncError}`); return; }
            transient++;
            if (!r.retried[it.subscriptionId]) { r.retried[it.subscriptionId] = true; r.queue.push(it.subscriptionId); return; }
            r.done++;
            r.retrying++;
            this.log("warn", `${it.companyName}: ${it.iuguSyncError || "IUGU instável"} (fica pendente)`);
          });
          (d.deferred || []).slice().reverse().forEach((id) => { r.queue.unshift(id); });
          (d.skipped || []).forEach((id) => {
            if (!r.retried[id]) { r.retried[id] = true; r.queue.push(id); return; }
            r.done++;
            r.skipped++;
          });
          const claimed = (d.items || []).length;
          r.idleRounds = claimed > 0 && transient === claimed ? r.idleRounds + 1 : 0;
          if (r.idleRounds >= 3) {
            r.paused = true;
            r.message = "A IUGU não está respondendo (limite de requisições ou instabilidade). Aguarde alguns minutos e clique em Continuar.";
            this.log("warn", r.message);
            return;
          }
          setTimeout(() => this.syncStep(runId), transient ? 5000 : 50);
        })
        .catch((e) => {
          if (this.runner.id !== runId) return;
          r.inFlight = false;
          r.queue = chunk.concat(r.queue);
          this.runnerHttpError(e);
        });
    },

    runnerHttpError(e) {
      const r = this.runner;
      const status = e && e.response ? e.response.status : 0;
      if (status === 401 || status === 403 || !this.password) {
        r.active = false;
        r.finished = true;
        r.message = status === 403 ? "Senha da migração inválida: entre de novo." : "Sessão encerrada.";
        this.log("err", r.message);
        return;
      }
      if (this.isTransientHttp(status)) {
        r.httpFailures++;
        const wait = this.retryWait(r.httpFailures);
        const runId = r.id;
        r.message = `A API respondeu ${status || "sem resposta"}${this.withDetail(e)}. Tentando de novo sozinho em ${wait}s (tentativa ${r.httpFailures}). Nada se perde.`;
        this.log("warn", r.message, e && e.incidentId);
        setTimeout(() => {
          if (this.runner.id !== runId || !r.active || r.paused) return;
          r.message = "";
          this.syncStep(runId);
        }, wait * 1000);
        return;
      }
      r.paused = true;
      r.message = `A API recusou a chamada (${status}${this.withDetail(e)}). Nada se perde: clique em Continuar para retomar.`;
      this.log("err", r.message, e && e.incidentId);
    },
    isTransientHttp(status) {
      return status === 0 || status === 408 || status === 429 || status >= 500;
    },
    retryWait(failures) {
      return Math.min(60, 5 * Math.pow(2, Math.min(failures - 1, 4)));
    },
    withDetail(e) {
      const d = e && e.response ? e.response.data : null;
      if (!d) return "";
      const text = typeof d === "string"
        ? d.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()
        : String(d.ExceptionMessage || d.exceptionMessage || d.Message || d.message || "");
      return text ? ": " + text.slice(0, 300) : "";
    },
    openServerQueue() {
      this.server.visible = true;
      this.pollServerQueue();
    },
    scheduleServerPoll(ms) {
      if (this.server.timer) clearTimeout(this.server.timer);
      this.server.timer = setTimeout(() => this.pollServerQueue(), ms);
    },
    pollServerQueue() {
      if (!this.password || this.server.destroyed) return Promise.resolve();
      if (this.server.timer) clearTimeout(this.server.timer);
      this.server.timer = null;
      return this.api("GET", MIGRACAO_IUGU_ASAAS_FILA_STATUS)
        .then((res) => {
          this.server.status = res.data || null;
          this.server.error = "";
          this.server.checkedAt = new Date().toLocaleTimeString("pt-BR");
          this.server.polls++;
          const c = this.serverCounts;
          const w = this.serverWorker;
          if (w.running || c.queued || c.processing || c.paused || c.awaitingCard) this.server.visible = true;
          const done = (w.ok || 0) + (w.failed || 0);
          const changed = done !== this.server.lastDone;
          this.server.lastDone = done;
          const active = this.serverBusyWork;
          if (this.server.visible) this.loadServerEvents();
          if (changed || (active && this.server.polls % 6 === 0)) this.load();
          else if (c.awaitingCard && this.server.polls % 3 === 0) this.loadCards();
          if (changed && this.history.loaded && !active) this.loadHistory();
          this.scheduleServerPoll(active ? SERVER_POLL_ACTIVE_MS : SERVER_POLL_IDLE_MS);
        })
        .catch((e) => {
          const status = e && e.response ? e.response.status : 0;
          if (status === 401 || status === 403) return;
          this.server.error = `Não consegui consultar a fila agora (${status || "sem resposta"}${this.withDetail(e)}). A fila continua no servidor; tento de novo em instantes.`;
          this.scheduleServerPoll(SERVER_POLL_ACTIVE_MS * 2);
        });
    },
    loadServerEvents() {
      return this.api("GET", MIGRACAO_IUGU_ASAAS_EVENTOS + "?take=40&tipos=" + SERVER_EVENT_TYPES)
        .then((res) => { this.server.events = res.data || []; })
        .catch(() => { });
    },
    serverEventClass(e) {
      if (e.type === "MIGRADA") return "log-ok";
      if (e.type === "ERRO") return "log-err";
      if (e.type === "RETENTATIVA" || e.type === "FILA_PAUSADA") return "log-warn";
      return "log-info";
    },
    pauseServerQueue() {
      this.server.busy = true;
      this.api("POST", MIGRACAO_IUGU_ASAAS_FILA_PAUSAR, {})
        .then((res) => {
          const affected = (res.data && res.data.affected) || 0;
          swal("Fila pausada", affected + " assinatura(s) saíram da fila sem nada criado. O que já estava em andamento termina normalmente. Use Retomar fila para continuar.", "info");
        })
        .catch((e) => swal("Aviso", "Não consegui pausar a fila" + this.withDetail(e) + ".", "error"))
        .finally(() => {
          this.server.busy = false;
          this.pollServerQueue();
          this.load();
        });
    },
    resumeServerQueue() {
      this.server.busy = true;
      this.api("POST", MIGRACAO_IUGU_ASAAS_FILA_RETOMAR, {})
        .then(() => { this.server.visible = true; })
        .catch((e) => swal("Aviso", "Não consegui retomar a fila" + this.withDetail(e) + ".", "error"))
        .finally(() => {
          this.server.busy = false;
          this.pollServerQueue();
          this.load();
        });
    },
    pauseRunner() {
      this.runner.paused = true;
      this.runner.message = "Pausado. O item em processamento termina normalmente; o restante fica na fila.";
    },
    resumeRunner() {
      const r = this.runner;
      r.paused = false;
      r.message = "";
      r.idleRounds = 0;
      if (r.inFlight) return;
      this.syncStep(r.id);
    },
    stopRunner() {
      const pending = this.runner.queue.length;
      this.runner.active = false;
      this.finishRunner(`Leitura parada: ${this.runner.ok} ok, ${this.runner.failed} com erro, ${pending} não lidas.`);
    },
    finishRunner(message) {
      const onDone = this.runner.onDone;
      this.runner.onDone = null;
      this.runner.active = false;
      this.runner.finished = true;
      this.runner.paused = false;
      this.runner.message = message;
      this.log("info", message);
      this.load().then(() => { if (onDone) onDone(); });
      if (this.history.loaded) this.loadHistory();
    },
    loadHistory() {
      if (this.history.loading) return Promise.resolve();
      this.history.loading = true;
      return this.api("GET", MIGRACAO_IUGU_ASAAS_EVENTOS + "?take=2000")
        .then((res) => {
          this.history.events = res.data || [];
          this.history.loaded = true;
          this.history.loadedAt = new Date().toLocaleTimeString("pt-BR");
        })
        .catch(() => { })
        .finally(() => { this.history.loading = false; });
    },
    historyCount(group) {
      if (!group.types) return this.history.events.length;
      return this.history.events.filter((e) => group.types.indexOf(e.type) !== -1).length;
    },
    eventLabel(type) {
      return EVENT_LABELS[type] || type;
    },
    openDrawerById(subscriptionId) {
      const item = this.items.find((i) => i.subscriptionId === subscriptionId) || this.allItems.find((i) => i.subscriptionId === subscriptionId);
      if (item) this.openDrawer(item);
    },
    exportHistory() {
      const header = ["Quando", "Empresa", "Id assinatura", "Evento", "Detalhe", "Quem"];
      const lines = this.historyRows.map((e) => [this.fmtDateTime(e.createdAt), e.companyName || "", e.subscriptionId, this.eventLabel(e.type), e.message || "", e.whoName || ""]);
      if (window.downloadListExcel) window.downloadListExcel(header, lines, "historico-migracao-iugu-asaas");
      else this.downloadCsv(header, lines, "historico-migracao-iugu-asaas");
    },
    exportCommsReport() {
      const wave = this.filters.wave;
      const rows = this.items.filter((i) => i.status === "MIGRADA"
        && (!wave || (wave === "__SEM__" ? !i.commsWave : i.commsWave === wave)));
      if (!rows.length) {
        swal("Nada para exportar", "Nenhuma assinatura migrada" + (wave ? " nesta leva" : "") + ".", "info");
        return;
      }
      const header = ["Leva de comunicação", "Última fatura paga na IUGU (vencimento)", "Empresa", "CNPJ", "E-mail", "E-mail financeiro", "Telefone", "Link master", "Gateway anterior", "Gateway novo",
        "Assinatura IUGU", "Assinatura ASAAS", "Valor mensal cobrado", "Vencimento IUGU (último)", "Dia de vencimento IUGU",
        "Dia de vencimento ASAAS", "Vencimento mudou", "Motivo da mudança", "1ª cobrança ASAAS", "Começa no ciclo seguinte",
        "MRR Master antes", "MRR Master depois", "Ajuste interno de MRR", "Link do 1º boleto ASAAS", "Migrada em", "Migrada por", "Texto sugerido para o cliente"];
      const asNumber = (v) => (v === null || v === undefined || v === "" ? "" : Number(v));
      const lines = rows.map((r) => {
        const iuguDay = this.dayOf(r.iuguExpiresAt);
        const asaasDay = this.dayOf(r.asaasNextDueDate);
        const dueChanged = !!(iuguDay && asaasDay && iuguDay !== asaasDay);
        const reason = !dueChanged ? "" : (iuguDay > 27 && asaasDay === 27 ? "Corte do dia 27: vencimentos após o dia 27 passam para o dia 27" : "Ajuste de vencimento na migração");
        return [
          r.commsWave || "", this.fmtDate(r.iuguPaidDueDate),
          r.companyName, r.cnpj || "", r.email || "", r.financialEmail || "", r.phone || "", r.masterUrl,
          "IUGU", "ASAAS (MAM)", r.iuguSubscriptionId || "", r.asaasSubscriptionId || "", asNumber(r.asaasValue),
          this.fmtDate(r.iuguExpiresAt), iuguDay || "", asaasDay || "", dueChanged ? "Sim" : "Não", reason,
          this.fmtDate(r.asaasNextDueDate), r.dueShiftedToNextCycle ? "Sim" : "Não",
          asNumber(r.mrrBefore), asNumber(r.mrrAfter), r.mrrAdjusted ? "Sim (Master passou a usar o valor que a IUGU já cobrava)" : "Não",
          r.asaasFirstPaymentUrl || "", this.fmtDateTime(r.migratedAt), r.migratedBy || "", this.commsText(r, iuguDay, asaasDay, dueChanged)
        ];
      });
      const name = "comunicacao-migracao-iugu-asaas" + (wave && wave !== "__SEM__" ? "-" + wave.replace(/[^\w-]+/g, "_") : "");
      if (window.downloadListExcel) window.downloadListExcel(header, lines, name);
      else this.downloadCsv(header, lines, name);
    },
    commsText(r, iuguDay, asaasDay, dueChanged) {
      const per = this.isNonMonthly(r) ? ({ 2: "por bimestre", 3: "por trimestre", 6: "por semestre", 12: "por ano" }[r.iuguCycleMonths] || "por ciclo") : "por mês";
      let text = "A partir de agora, a cobrança da MarQ passa a ser emitida pela plataforma ASAAS, por boleto, no mesmo valor de " + this.money(r.asaasValue) + " " + per + ".";
      if (this.isNonMonthly(r)) text += " Seu plano " + this.planTypeLabel(r.planType) + " continua igual.";
      text += " A primeira cobrança pelo ASAAS vence em " + this.fmtDate(r.asaasNextDueDate) + ".";
      if (dueChanged) text += " O vencimento mensal passa do dia " + iuguDay + " para o dia " + asaasDay + ".";
      if (r.dueShiftedToNextCycle) text += " O boleto de " + this.fmtDate(r.iuguExpiresAt) + " já emitido pela plataforma anterior continua válido e deve ser pago normalmente.";
      else if (r.iuguPaidDueDate && r.asaasNextDueDate && String(r.iuguPaidDueDate).slice(0, 10) < String(r.asaasNextDueDate).slice(0, 10))
        text += " A fatura com vencimento em " + this.fmtDate(r.iuguPaidDueDate) + " já foi paga na plataforma anterior e segue quitada; nada muda nela.";
      return text;
    },
    closeRunner() {
      this.runner = emptyRunner();
    },
    log(kind, text, incident) {
      const now = new Date();
      this.runner.log.unshift({ kind: kind, text: text, time: now.toLocaleTimeString("pt-BR"), incident: incident || null });
      if (this.runner.log.length > 200) this.runner.log.pop();
    },

    openSimulate(ids, forceSuspended) {
      if (!ids || !ids.length) return;
      this.modal = emptyModal();
      this.modal.type = "simulate";
      this.modal.forceSuspended = !!forceSuspended;
      this.modal.ids = ids.slice();
      this.modal.queue = ids.slice();
      this.modal.running = true;
      this.simulateStep(this.modal.id);
    },
    simulateStep(modalId) {
      const m = this.modal;
      if (m.id !== modalId || m.type !== "simulate") return;
      const chunk = m.queue.splice(0, SIMULATE_CHUNK);
      if (!chunk.length) {
        m.running = false;
        this.load();
        if (this.history.loaded) this.loadHistory();
        return;
      }
      this.api("POST", MIGRACAO_IUGU_ASAAS_SIMULAR, { subscriptionIds: chunk, forceSuspended: !!m.forceSuspended, segment: this.segment })
        .then((res) => {
          if (this.modal.id !== modalId) return;
          m.httpFailures = 0;
          let deferred = 0;
          (res.data || []).forEach((p) => {
            const tries = m.simRetries[p.subscriptionId] || 0;
            if (p.errorCode === "SIMULACAO_ADIADA" && tries < SIMULATE_MAX_RETRIES) {
              m.simRetries[p.subscriptionId] = tries + 1;
              m.queue.push(p.subscriptionId);
              deferred++;
              return;
            }
            m.plans.push(p);
          });
          m.done = m.plans.length;
          m.deferredCount = m.queue.filter((id) => m.simRetries[id]).length;
          setTimeout(() => this.simulateStep(modalId), deferred ? 3000 : 0);
        })
        .catch((e) => {
          if (this.modal.id !== modalId) return;
          m.queue = chunk.concat(m.queue);
          const status = e && e.response ? e.response.status : 0;
          if (status !== 401 && status !== 403 && this.isTransientHttp(status) && m.httpFailures < 20) {
            m.httpFailures++;
            const wait = this.retryWait(m.httpFailures);
            m.retryMessage = `A API respondeu ${status || "sem resposta"}${this.withDetail(e)}. Tentando de novo sozinho em ${wait}s (tentativa ${m.httpFailures}).`;
            setTimeout(() => {
              if (this.modal.id !== modalId) return;
              m.retryMessage = "";
              this.simulateStep(modalId);
            }, wait * 1000);
            return;
          }
          m.running = false;
          swal("Aviso", `A simulação parou em ${m.plans.length} de ${m.ids.length}${this.withDetail(e)}. Feche e simule de novo as que faltaram.`, "error");
        });
    },
    switchToSimulate() {
      this.openSimulate(this.modal.ids, this.modal.forceSuspended);
    },
    exportSimulation() {
      const byId = {};
      this.items.forEach((i) => { byId[i.subscriptionId] = i; });
      const header = ["Id da empresa", "Id assinatura", "Nome", "Resultado", "Motivo", "MRR Master", "MRR IUGU", "Valor ASAAS", "Venc. IUGU", "1º venc. ASAAS", "Dia ajustado p/ 27", "Ciclo seguinte", "Avisos"];
      const lines = this.modal.plans.map((p) => {
        const i = byId[p.subscriptionId] || {};
        return [i.companyId, p.subscriptionId, p.companyName, p.canMigrate ? "Pronta" : "Bloqueada",
          p.canMigrate ? "" : `${p.errorCode}: ${p.errorMessage}`, this.num(p.mrrMaster), this.num(p.iuguMrr), this.num(p.asaasValue),
          this.fmtDate(p.iuguExpiresAt), this.fmtDate(p.asaasNextDueDate), p.dueDayCapped ? "Sim" : "Não",
          p.dueShiftedToNextCycle ? "Sim" : "Não", (p.warnings || []).join(" | ")];
      });
      this.downloadCsv(header, lines, "simulacao-migracao-iugu-asaas");
    },

    isIuguStopped(r) {
      if (!r) return false;
      if (r.iuguSuspended === true || r.iuguActive === false || (r.status === "ERRO" && FORCE_CODES.indexOf(r.errorCode) !== -1)) return true;
      if (this.isStaleLink(r)) return true;
      return !!r.iuguExpiresAt && !r.iuguCycleInvoiced && this.dateKey(r.iuguExpiresAt) < this.isoDate(new Date());
    },
    openMigrate(ids, forceSuspended) {
      if (!ids || !ids.length) return;
      const set = {};
      ids.forEach((id) => { set[id] = true; });
      const rows = this.items.filter((i) => set[i.subscriptionId]);
      const blocked = rows.filter((i) => i.status === "MIGRADA" || i.status === "EXCLUIDA" || i.status === "PROCESSANDO");
      if (blocked.length === rows.length) {
        swal("Nada a migrar", "As selecionadas já estão migradas, excluídas ou em processamento.", "info");
        return;
      }
      this.modal = emptyModal();
      this.modal.type = "migrate";
      this.modal.ids = rows.filter((i) => blocked.indexOf(i) === -1).map((i) => i.subscriptionId);
      this.modal.unsynced = rows.filter((i) => i.iuguSyncStatus !== "OK").length;
      this.modal.wave = this.filters.wave && this.filters.wave !== "__SEM__" ? this.filters.wave : this.suggestedWave();
      const chosen = {};
      this.modal.ids.forEach((id) => { chosen[id] = true; });
      this.modal.suspendedCount = rows.filter((i) => chosen[i.subscriptionId] && this.isIuguStopped(i)).length;
      this.modal.staleLinkCount = rows.filter((i) => chosen[i.subscriptionId] && (this.isStaleLink(i) || (i.status === "ERRO" && RELINK_CODES.indexOf(i.errorCode) !== -1))).length;
      this.modal.forceSuspended = !!forceSuspended;
    },
    confirmMigrate() {
      this.modal.running = true;
      const ids = this.modal.ids.slice();
      const force = !!this.modal.forceSuspended;
      this.api("POST", MIGRACAO_IUGU_ASAAS_ENFILEIRAR, { subscriptionIds: ids, batch: (this.modal.wave || "").trim(), forceSuspended: force, reason: force ? this.modal.forceReason.trim() : "", segment: this.segment })
        .then((res) => {
          const affected = (res.data && res.data.affected) || 0;
          this.closeModal();
          this.clearSelection();
          return this.load().then(() => {
            if (!affected) {
              swal("Nada enviado", "Nenhuma das selecionadas estava apta a entrar na fila.", "info");
              return;
            }
            this.openServerQueue();
          });
        })
        .catch(() => swal("Aviso", "Falha ao enviar para a fila.", "error"))
        .finally(() => { this.modal.running = false; });
    },

    openExclude(ids) {
      if (!ids || !ids.length) return;
      this.modal = emptyModal();
      this.modal.type = "exclude";
      this.modal.ids = ids.slice();
    },
    confirmExclude() {
      this.modal.running = true;
      this.api("POST", MIGRACAO_IUGU_ASAAS_EXCLUIR, { subscriptionIds: this.modal.ids, reason: this.modal.reason })
        .then((res) => {
          const affected = (res.data && res.data.affected) || 0;
          const skipped = this.modal.ids.length - affected;
          swal("Pronto", `${affected} excluída(s) da migração.` + (skipped ? ` ${skipped} não puderam ser excluídas (já migradas, em processamento ou com algo criado no ASAAS).` : ""), skipped ? "warning" : "success");
          this.closeModal();
          this.clearSelection();
          this.load();
        })
        .catch(() => swal("Aviso", "Falha ao excluir.", "error"))
        .finally(() => { this.modal.running = false; });
    },
    include(ids) {
      if (!ids || !ids.length) return;
      this.api("POST", MIGRACAO_IUGU_ASAAS_REINCLUIR, { subscriptionIds: ids })
        .then((res) => {
          swal("Pronto", `${(res.data && res.data.affected) || 0} reincluída(s) na migração.`, "success");
          this.load();
        })
        .catch(() => swal("Aviso", "Falha ao reincluir.", "error"));
    },

    openRollback(ids) {
      if (!ids || !ids.length) return;
      const set = {};
      ids.forEach((id) => { set[id] = true; });
      const eligible = this.items.filter((i) => set[i.subscriptionId] && i.asaasSubscriptionId && (i.status === "MIGRADA" || i.status === "ERRO"));
      if (!eligible.length) {
        swal("Nada a reverter", "Só dá para reverter itens migrados ou com erro que já tenham assinatura ASAAS.", "info");
        return;
      }
      this.modal = emptyModal();
      this.modal.type = "rollback";
      this.modal.ids = eligible.map((i) => i.subscriptionId);
    },
    confirmRollback() {
      this.modal.running = true;
      const ids = this.modal.ids.slice();
      const total = { ok: 0, failed: 0 };
      const next = (offset) => {
        const chunk = ids.slice(offset, offset + 10);
        if (!chunk.length) {
          this.modal.result = total;
          this.modal.running = false;
          this.load();
          return;
        }
        this.api("POST", MIGRACAO_IUGU_ASAAS_REVERTER, { subscriptionIds: chunk, reason: this.modal.reason })
          .then((res) => {
            const d = res.data || {};
            total.ok += d.ok || 0;
            total.failed += d.failed || 0;
            this.mergeItems(d.items);
            next(offset + 10);
          })
          .catch(() => {
            this.modal.result = total;
            this.modal.running = false;
            swal("Aviso", `A reversão parou após ${offset} item(ns). Confira o histórico e tente de novo.`, "error");
          });
      };
      next(0);
    },

    openPaste() {
      this.modal = emptyModal();
      this.modal.type = "paste";
    },
    confirmPaste() {
      const text = this.modal.pasteText;
      this.parseCsv("lista colada", text.split(/\r?\n/).map((l) => l.split("\t")[0]).join("\n"));
    },
    openCsvImport() {
      this.$refs.csvInput.value = "";
      this.$refs.csvInput.click();
    },
    onCsvFile(e) {
      const file = e.target.files && e.target.files[0];
      if (!file) return;
      const reader = new FileReader();
      reader.onload = () => this.parseCsv(file.name, String(reader.result || ""));
      reader.onerror = () => swal("Aviso", "Não foi possível ler o arquivo.", "error");
      reader.readAsText(file, "utf-8");
    },
    parseCsv(fileName, text) {
      const lines = text.replace(/^﻿/, "").split(/\r?\n/).filter((l) => l.trim().length);
      if (!lines.length) {
        swal("Aviso", "Arquivo vazio.", "warning");
        return;
      }
      const sep = (lines[0].match(/;/g) || []).length >= (lines[0].match(/,/g) || []).length ? ";" : ",";
      const clean = (v) => (v || "").trim().replace(/^"|"$/g, "").trim();
      const norm = (v) => clean(v).toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "").replace(/[^a-z]/g, "");
      const header = lines[0].split(sep).map(norm);
      const subNames = ["idassinatura", "subscriptionid", "assinatura", "idsubscription"];
      const compNames = ["iddaempresa", "idempresa", "companyid", "empresaid"];
      const docNames = ["cnpj", "cpfcnpj", "cnpjcpf", "cpf", "documento", "cnpjdaempresa", "cnpjempresa"];
      let col = header.findIndex((h) => subNames.indexOf(h) !== -1);
      let kind = "subscription";
      if (col === -1) {
        col = header.findIndex((h) => compNames.indexOf(h) !== -1);
        kind = "company";
      }
      if (col === -1) {
        col = header.findIndex((h) => docNames.indexOf(h) !== -1);
        kind = "document";
      }
      let rows = lines.slice(1);
      if (col === -1) {
        col = 0;
        kind = "auto";
        rows = lines;
      }
      const values = rows.map((l) => clean(l.split(sep)[col])).filter((v) => v);
      const bySub = {};
      const byCompany = {};
      const byDocument = {};
      const docKey = (v) => {
        const digits = String(v || "").replace(/\D/g, "");
        if (!digits) return "";
        if (digits.length > 11 && digits.length < 14) return ("00000000000000" + digits).slice(-14);
        if (digits.length > 8 && digits.length < 11) return ("00000000000" + digits).slice(-11);
        return digits;
      };
      this.items.forEach((i) => {
        bySub[i.subscriptionId.toUpperCase()] = i.subscriptionId;
        const key = (i.companyId || "").toUpperCase();
        if (!byCompany[key]) byCompany[key] = [];
        byCompany[key].push(i.subscriptionId);
        const doc = docKey(i.cnpj);
        if (doc) {
          if (!byDocument[doc]) byDocument[doc] = [];
          byDocument[doc].push(i.subscriptionId);
        }
      });
      const matched = {};
      const unmatched = [];
      values.forEach((v) => {
        const key = v.toUpperCase();
        if ((kind === "subscription" || kind === "auto") && bySub[key]) { matched[bySub[key]] = true; return; }
        if ((kind === "company" || kind === "auto") && byCompany[key]) { byCompany[key].forEach((id) => { matched[id] = true; }); return; }
        const doc = docKey(v);
        if ((kind === "document" || kind === "auto") && doc && byDocument[doc]) { byDocument[doc].forEach((id) => { matched[id] = true; }); return; }
        unmatched.push(v);
      });
      this.modal = emptyModal();
      this.modal.type = "csv";
      this.modal.fileName = fileName;
      this.modal.csvColumn = kind === "auto" ? "1ª coluna (sem cabeçalho reconhecido)" : (kind === "company" ? "Id da empresa" : (kind === "document" ? "CNPJ" : "Id assinatura"));
      this.modal.csvTotal = values.length;
      this.modal.matched = Object.keys(matched);
      this.modal.unmatched = unmatched;
    },
    csvSelect() {
      this.selectIds(this.modal.matched);
      this.filters = this.defaultFilters();
      this.filters.status = "TODOS";
      this.closeModal();
    },
    csvExclude() {
      const ids = this.modal.matched.slice();
      this.openExclude(ids);
    },
    csvSimulate() {
      const ids = this.modal.matched.slice();
      this.selectIds(ids);
      this.openSimulate(ids);
    },

    closeModal() {
      this.modal = emptyModal();
    },
    migrateReady() {
      this.openMigrate(this.simReady.map((p) => p.subscriptionId));
    },
    openDrawer(item) {
      this.drawer.item = item;
      this.drawer.events = [];
      this.drawer.loading = true;
      this.api("GET", `${MIGRACAO_IUGU_ASAAS_ITENS}/${encodeURIComponent(item.subscriptionId)}/eventos`)
        .then((r) => { this.drawer.events = r.data || []; })
        .catch(() => { this.drawer.events = []; })
        .finally(() => { this.drawer.loading = false; });
    },

    openExport() {
      this.modal = emptyModal();
      this.modal.type = "export";
      this.modal.exportScope = this.selectedCount ? "selecao" : "filtro";
      this.modal.exportRefresh = !this.runner.active;
    },
    confirmExport() {
      const ids = this.exportRows.map((r) => r.subscriptionId);
      const format = this.modal.exportFormat;
      const refresh = this.modal.exportRefresh;
      this.closeModal();
      const generate = () => this.generateExport(ids, format);
      if (refresh) this.startSync(ids, generate);
      else generate();
    },
    generateExport(ids, format) {
      const byId = {};
      this.items.forEach((i) => { byId[i.subscriptionId] = i; });
      const rows = ids.map((id) => byId[id]).filter((r) => r);
      const header = ["Id da empresa", "Id assinatura", "Nome", "Link master", "MRR assinatura", "MRR assinatura IUGU",
        "Data vencimento IUGU", "Telefone", "Email", "Email financeiro", "Ultimo boleto gerado"];
      const asCsv = format === "csv";
      const money = (v) => (v === null || v === undefined || v === "" ? "" : (asCsv ? this.num(v) : Number(v)));
      let missing = 0;
      const lines = rows.map((r) => {
        const hasIugu = r.iuguSyncStatus === "OK" && r.iuguMrr !== null && r.iuguMrr !== undefined;
        if (!hasIugu) missing++;
        const iuguProblem = r.iuguSyncStatus === "ERRO"
          ? "erro na IUGU: " + (r.iuguSyncError || "sem detalhe")
          : (r.iuguSyncError ? "IUGU não respondeu: " + r.iuguSyncError : "não lido na IUGU");
        return [
          r.companyId,
          r.subscriptionId,
          r.companyName,
          r.masterUrl,
          money(r.mrrMaster),
          hasIugu ? money(r.iuguMrr) : iuguProblem,
          hasIugu ? this.fmtDate(r.iuguExpiresAt) : "",
          r.phone || "",
          r.email || "",
          r.financialEmail || "",
          this.fmtDate(r.iuguLastInvoiceDueDate)
        ];
      });
      const name = "migracao-iugu-asaas";
      if (asCsv || !window.downloadListExcel) this.downloadCsv(header, lines, name);
      else window.downloadListExcel(header, lines, name);
      if (missing)
        swal("Planilha gerada com pendências", missing + " de " + rows.length + " assinatura(s) estão sem dado da IUGU. A coluna 'MRR assinatura IUGU' traz o motivo de cada uma; dá para tentar de novo exportando só essas.", "warning");
    },
    downloadCsv(header, lines, name) {
      const esc = (v) => {
        const s = v === null || v === undefined ? "" : String(v);
        return '"' + s.replace(/"/g, '""') + '"';
      };
      const csv = [header.map(esc).join(";")].concat(lines.map((l) => l.map(esc).join(";"))).join("\r\n");
      const blob = new Blob(["﻿" + csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${name}-${this.isoDate(new Date())}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },

    stepClass(n) {
      const s = this.stats;
      const done = {
        1: this.items.length > 0,
        2: this.items.length > 0 && s.syncPending === 0,
        3: !!(this.filters.dueFrom || this.filters.dueTo),
        4: s.migrated > 0,
        5: s.migrated > 0 && s.errors === 0
      };
      let current = 1;
      while (current <= 5 && done[current]) current++;
      return { done: done[n], current: n === current };
    },
    statusKey(r) {
      if (this.isCritical(r)) return "critical";
      if (!this.inScope(r) && r.status !== "EXCLUIDA") return "fora";
      return (r.status || "").toLowerCase();
    },
    statusLabel(r) {
      if (!this.inScope(r) && r.status !== "EXCLUIDA") return "Saiu do critério";
      if (this.isCritical(r)) return r.status === "ERRO" ? "Incompleta (ASAAS criado)" : "Retomando";
      return STATUS_LABEL[r.status] || r.status;
    },
    stepLabel(step) {
      return STEP_LABEL[step] || "Nenhuma";
    },
    errorLabel(code) {
      const e = ERRORS[code || "INESPERADO"];
      return e ? e[0] : code;
    },
    errorHelp(code) {
      const e = ERRORS[code || "INESPERADO"];
      return e ? e[1] : "";
    },
    onNameClick(r) {
      if (nameClickTimer) clearTimeout(nameClickTimer);
      nameClickTimer = setTimeout(() => {
        nameClickTimer = null;
        this.openDrawer(r);
      }, 250);
    },
    onNameDblClick(r) {
      if (nameClickTimer) clearTimeout(nameClickTimer);
      nameClickTimer = null;
      if (r && r.masterUrl) window.open(r.masterUrl, "_blank");
    },
    invoiceTone(status) {
      const s = (status || "").toLowerCase();
      if (s === "paid" || s === "externally_paid" || s === "partially_paid") return "paid";
      if (s === "canceled" || s === "refunded" || s === "chargeback") return "canceled";
      if (s === "pending" || s === "in_protest") return "pending";
      if (s === "expired") return "expired";
      return "none";
    },
    invoiceStatusLabel(status) {
      const map = { pending: "pendente", paid: "paga", canceled: "cancelada", expired: "expirada", partially_paid: "paga parcial", refunded: "estornada", externally_paid: "paga por fora", in_protest: "em protesto", chargeback: "chargeback" };
      return map[status] || status || "";
    },
    share(n) {
      return this.stats.scope ? (100 * n / this.stats.scope).toFixed(2) + "%" : "0%";
    },
    pct(n, total) {
      if (!total) return 0;
      const v = 100 * n / total;
      return v >= 10 || v === 0 ? Math.floor(v) : v.toFixed(1);
    },
    dateOnly(v) {
      return v ? String(v).substring(0, 10) : "";
    },
    dayOf(v) {
      const d = this.dateOnly(v);
      return d ? Number(d.substring(8, 10)) : 0;
    },
    fmtDate(v) {
      const d = this.dateOnly(v);
      if (!d) return "";
      return `${d.substring(8, 10)}/${d.substring(5, 7)}/${d.substring(0, 4)}`;
    },
    fmtDateTime(v) {
      if (!v) return "";
      return new Date(v).toLocaleString("pt-BR");
    },
    fmtTime(v) {
      if (!v) return "";
      return new Date(v).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    },
    isoDate(d) {
      const p = (n) => (n < 10 ? "0" + n : "" + n);
      return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
    },
    addDays(d, days) {
      const copy = new Date(d.getFullYear(), d.getMonth(), d.getDate());
      copy.setDate(copy.getDate() + days);
      return copy;
    },
    money(v) {
      return "R$ " + Number(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    },
    excelText(v) {
      return v ? `="${v}"` : "";
    },
    num(v) {
      if (v === null || v === undefined || v === "") return "";
      return Number(v).toFixed(2).replace(".", ",");
    },
    asaasSubLink(r) {
      return `https://www.asaas.com/subscription/show/${r.asaasSubscriptionId}`;
    },
    asaasCliLink(r) {
      return `https://www.asaas.com/customerAccount/show/${r.asaasCustomerId}`;
    }
  }
};
</script>

<style lang="scss" scoped>
$blue: #1B59F8;
$green: #12B937;
$red: #FF445D;
$amber: #f4a100;
$gray: #90a4ae;
$ink: #263238;

.mig-page {
  .page-content { padding: 20px 20px 90px; }
  .txt-danger { color: $red; font-weight: 600; }
  .txt-warn { color: darken($amber, 8%); font-weight: 600; }
  .muted { color: #999; }
  a { color: $blue; cursor: pointer; text-decoration: none; }

  .btn-primary, .btn-secondary, .btn-danger {
    border-radius: 4px; padding: 6px 14px; font-size: 13px; font-weight: 600; cursor: pointer;
    &:disabled { opacity: .5; cursor: not-allowed; }
  }
  .btn-primary { background: $blue; color: #fff; border: 1px solid $blue; }
  .btn-secondary { background: #fff; color: $blue; border: 1px solid $blue; }
  .btn-danger { background: #fff; color: $red; border: 1px solid $red; }
  .btn-link { background: none; border: none; color: #666; cursor: pointer; font-size: 13px; }

  .gate { display: flex; justify-content: center; padding: 60px 0; }
  .gate-card {
    background: #fff; border-radius: 8px; padding: 28px 32px; width: 420px; max-width: 100%;
    box-shadow: 0 2px 10px rgba(0,0,0,.08); display: flex; flex-direction: column; gap: 12px;
    h3 { margin: 0; color: $ink; }
    p { color: #555; font-size: 13px; margin: 0; }
    input { border: 1px solid #ccc; border-radius: 4px; padding: 8px 10px; font-size: 14px; }
  }

  .hero {
    display: flex; gap: 16px; flex-wrap: wrap; margin-bottom: 14px;
    .hero-main {
      flex: 2 1 460px; background: #fff; border-radius: 8px; padding: 16px 20px; box-shadow: 0 1px 3px rgba(0,0,0,.08);
    }
    .hero-title { display: flex; align-items: baseline; gap: 8px; color: #555; font-size: 14px;
      .big { font-size: 32px; font-weight: 700; color: $ink; }
      .pct { margin-left: auto; font-size: 24px; font-weight: 700; color: $green; }
    }
    .segbar { display: flex; height: 14px; background: #eceff1; border-radius: 7px; overflow: hidden; margin: 10px 0 8px; }
    .seg { height: 100%; transition: width .4s; }
    .seg-ok { background: $green; } .seg-run { background: $blue; } .seg-err { background: $red; }
    .legend { display: flex; flex-wrap: wrap; gap: 14px; font-size: 12px; color: #555;
      .lg { cursor: pointer; display: inline-flex; align-items: center; gap: 5px;
        &:before { content: ""; width: 10px; height: 10px; border-radius: 2px; display: inline-block; }
      }
      .lg-ok:before { background: $green; } .lg-run:before { background: $blue; } .lg-err:before { background: $red; }
      .lg-pend:before { background: #eceff1; border: 1px solid #ccc; } .lg-exc:before { background: $gray; }
    }
    .hero-side { flex: 1 1 300px; display: flex; flex-direction: column; gap: 8px; }
    .kpi {
      background: #fff; border-radius: 8px; padding: 10px 14px; box-shadow: 0 1px 3px rgba(0,0,0,.08);
      display: flex; flex-direction: column;
      .kpi-label { font-size: 12px; color: #666; }
      .kpi-value { font-size: 20px; font-weight: 700; color: $ink; }
      .kpi-sub { font-size: 11px; color: #999; }
      &.kpi-alert { border-left: 4px solid $red; cursor: pointer; .kpi-value { color: $red; } }
    }
  }

  .steps {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(210px, 1fr)); gap: 10px; margin-bottom: 14px;
    .step {
      background: #fff; border-radius: 8px; padding: 10px 12px; box-shadow: 0 1px 3px rgba(0,0,0,.06);
      display: flex; gap: 10px; align-items: flex-start; flex-wrap: wrap; border-top: 3px solid #eceff1;
      .n { width: 24px; height: 24px; border-radius: 50%; background: #eceff1; color: #555; font-weight: 700;
        display: inline-flex; align-items: center; justify-content: center; flex-shrink: 0; }
      div { flex: 1; min-width: 140px; }
      strong { display: block; font-size: 13px; color: $ink; }
      small { color: #777; font-size: 11px; }
      button { margin-left: 34px; }
      &.done { border-top-color: $green; .n { background: $green; color: #fff; } }
      &.current { border-top-color: $blue; .n { background: $blue; color: #fff; } }
    }
  }

  .banner { border-radius: 6px; padding: 10px 14px; font-size: 13px; margin-bottom: 12px; }
  .banner-err { background: #fdecee; color: #b71c1c; border: 1px solid #f8c7cd; }

  .server-queue .runner-log a { cursor: pointer; }
  .laudo-link { cursor: pointer; font-size: 11px; margin-left: 6px; text-decoration: underline; }
  .laudo-box { white-space: pre-wrap; word-break: break-word; font-size: 11px; max-height: 420px; overflow: auto; background: #f7f9fb; border: 1px solid #e0e6ea; border-radius: 4px; padding: 10px; }
  .ec-action { margin-top: 6px; font-size: 11px; padding: 2px 8px; }
  .tag.tag-wave { background: #ede7f6; color: #5e35b1; }
  .force-box { border: 1px solid $amber; background: #fff8e8; border-radius: 6px; padding: 10px 12px; margin: 12px 0;
    input[type=text] { width: 100%; box-sizing: border-box; margin-top: 6px; }
    p { margin: 6px 0 0; }
  }
  .ok-text { color: #2e7d32; }
  .card-box .chip.warn { border-color: $amber; color: #a66d00; font-weight: 600; }
  .card-box .chip.on.warn { color: #fff; }
  .card-bulk { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 8px; font-size: 13px; }
  .txt-ok { color: #2e7d32; }
  .txt-warn { color: #a66d00; font-size: 12px; }
  .runner {
    background: #fff; border-radius: 8px; padding: 12px 16px; margin-bottom: 14px; border-left: 4px solid $blue;
    box-shadow: 0 1px 3px rgba(0,0,0,.08);
    &.paused { border-left-color: $amber; }
    .runner-head { display: flex; gap: 14px; align-items: center; flex-wrap: wrap; font-size: 13px;
      .ok { color: $green; font-weight: 600; } .err { color: $red; font-weight: 600; } .warn { color: $amber; font-weight: 600; }
      .runner-actions { margin-left: auto; display: flex; gap: 8px; }
    }
    .runner-msg { font-size: 12px; color: #666; margin: 6px 0 0; }
    .runner-log { list-style: none; margin: 8px 0 0; padding: 0; max-height: 160px; overflow-y: auto; font-size: 12px;
      li { padding: 2px 0; border-bottom: 1px dashed #f0f0f0; }
      .t { color: #aaa; margin-right: 6px; }
      .log-ok { color: #2e7d32; } .log-err { color: #c62828; } .log-warn { color: #a66d00; } .log-info { color: #555; }
    }
  }
  .bar { height: 8px; background: #eceff1; border-radius: 4px; overflow: hidden; margin-top: 8px;
    .bar-fill { height: 100%; background: $blue; transition: width .3s; }
  }

  .errors-box {
    background: #fff; border-radius: 8px; padding: 12px 16px; margin-bottom: 14px; box-shadow: 0 1px 3px rgba(0,0,0,.06);
    .errors-head { display: flex; gap: 10px; align-items: baseline; margin-bottom: 8px; small { color: #888; } }
    .error-chips { display: flex; flex-wrap: wrap; gap: 8px; }
    .error-chip {
      border: 1px solid #f3c2c9; background: #fff7f8; border-radius: 6px; padding: 6px 10px; cursor: pointer; max-width: 320px;
      .ec-top { display: flex; justify-content: space-between; gap: 10px; font-size: 12px; font-weight: 700; color: #b71c1c; }
      .ec-count { background: $red; color: #fff; border-radius: 10px; padding: 0 7px; }
      .ec-help { font-size: 11px; color: #666; margin-top: 2px; }
      &.critical { border-color: $red; background: #fdecee; }
      &.active { outline: 2px solid $blue; }
    }
  }

  .filters {
    background: #fff; border-radius: 8px; padding: 12px 16px; margin-bottom: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.06);
    display: flex; flex-wrap: wrap; gap: 12px; align-items: flex-end;
    .f { display: flex; flex-direction: column; gap: 3px;
      label { font-size: 11px; color: #777; font-weight: 500; }
      input[type=text], input[type=date], select { border: 1px solid #ccc; border-radius: 4px; padding: 5px 8px; font-size: 13px; min-width: 130px; }
      &.checks { gap: 2px; label { display: flex; align-items: center; gap: 5px; font-size: 12px; color: #444; } }
      &.actions { flex-direction: row; gap: 8px; margin-left: auto; }
    }
    .chips { display: flex; gap: 6px; flex-wrap: wrap; }
    .chip { border: 1px solid #cfd8dc; border-radius: 12px; padding: 3px 10px; font-size: 12px; cursor: pointer; background: #fafafa;
      &:hover { border-color: $blue; color: $blue; }
    }
  }
  .window-info { font-size: 12px; color: #555; margin: 0 0 8px 4px; }
  .seg-tabs { display: flex; gap: 8px; margin: 0 0 10px;
    .seg-tab { display: flex; align-items: baseline; gap: 6px; padding: 8px 14px; border: 1px solid #d6dbe3; border-radius: 6px; background: #fff; color: #444; cursor: pointer; text-decoration: none;
      b { color: $ink; } small { color: #888; }
      &.on { border-color: $blue; box-shadow: inset 0 -3px 0 $blue; color: $ink; font-weight: 600; }
    }
  }
  .seg-note { font-size: 12px; color: #4a4a4a; background: #f3f6fd; border: 1px solid #d6e0f5; border-radius: 6px; padding: 8px 12px; margin: 0 0 12px; }
  .first-charge { .opt { display: block; font-weight: normal; margin: 4px 0; } input[type=date] { margin-left: 8px; } }
  .relink-banner { display: flex; align-items: center; justify-content: space-between; gap: 12px; flex-wrap: wrap;
    border: 1px solid #f0c36d; background: #fff8e8; border-radius: 6px; padding: 10px 14px; margin: 0 0 12px;
    small { display: block; color: #6d5a2e; margin-top: 2px; }
    .relink-actions { display: flex; gap: 8px; }
  }

  .toolbar { display: flex; justify-content: space-between; align-items: center; margin: 6px 0 8px; flex-wrap: wrap; gap: 8px;
    .count { font-size: 13px; color: #555; }
    .toolbar-right { display: flex; gap: 8px; }
  }
  .select-banner { background: #eef3ff; border: 1px solid #cddcff; border-radius: 6px; padding: 6px 12px; font-size: 12px; margin-bottom: 8px; text-align: center; }

  .table-wrap { overflow-x: auto; background: #fff; border-radius: 8px; box-shadow: 0 1px 3px rgba(0,0,0,.06); max-height: 70vh; overflow-y: auto; }
  .grid {
    width: 100%; border-collapse: collapse; font-size: 12px;
    th, td { padding: 7px 10px; border-bottom: 1px solid #eee; text-align: left; vertical-align: top; white-space: nowrap; }
    th { background: #fafafa; color: #555; font-weight: 600; position: sticky; top: 0; z-index: 2; }
    th.sortable { cursor: pointer; user-select: none; }
    .num { text-align: right; }
    .sticky-col { position: sticky; left: 0; background: #fff; z-index: 1; }
    th.sticky-col { z-index: 3; background: #fafafa; }
    .company { white-space: normal; min-width: 220px;
      .name { font-weight: 600; color: $ink; cursor: pointer; user-select: none; }
      .ext { margin-left: 4px; font-size: 11px; }
    }
    .inv-date { font-weight: 600; }
    .inv-pill { display: inline-block; padding: 0 6px; border-radius: 4px; font-weight: 600; }
    .inv-date.inv-paid { color: #2e7d32; } .inv-pill.inv-paid { background: #e6f4ea; color: #2e7d32; }
    .inv-date.inv-canceled { color: #c62828; } .inv-pill.inv-canceled { background: #fdecee; color: #c62828; }
    .inv-date.inv-pending { color: #a66d00; } .inv-pill.inv-pending { background: #fff4e0; color: #a66d00; }
    .inv-date.inv-expired { color: $blue; } .inv-pill.inv-expired { background: #e8f0ff; color: $blue; }
    .mail { max-width: 220px; overflow: hidden; text-overflow: ellipsis; }
    .sub { font-size: 11px; color: #888; margin-top: 2px;
      &.err { color: $red; } &.warn { color: $amber; }
    }
    .row-selected td { background: #eef3ff; }
    .row-critical td { background: #fff3f4; }
  }
  .badge { display: inline-block; padding: 2px 8px; border-radius: 10px; font-size: 11px; font-weight: 700; color: #fff; background: $gray;
    &.b-pendente { background: #b0bec5; }
    &.b-na_fila, &.b-processando, &.b-revertendo { background: $blue; }
    &.b-aguardando_cartao { background: $amber; }
    &.b-migrada { background: $green; }
    &.b-erro { background: $red; }
    &.b-critical { background: #b71c1c; }
    &.b-excluida { background: #78909c; }
    &.b-revertida { background: #7e57c2; }
    &.b-fora { background: #cfd8dc; color: #455a64; }
  }
  .tag { display: inline-block; margin: 2px 4px 0 0; padding: 0 6px; border-radius: 4px; font-size: 10px; font-weight: 600;
    &.tag-warn { background: #fff4e0; color: #a66d00; }
    &.tag-info { background: #e8f0ff; color: $blue; }
    &.tag-muted { background: #eceff1; color: #607d8b; }
    &.tag-err { background: #fdecee; color: #c62828; }
  }
  .empty { color: #999; text-align: center; padding: 30px 0; }

  .pager { display: flex; gap: 10px; align-items: center; justify-content: flex-end; margin-top: 10px; font-size: 13px;
    select { border: 1px solid #ccc; border-radius: 4px; padding: 4px 6px; }
  }

  .action-bar {
    position: fixed; left: 50%; bottom: 18px; transform: translateX(-50%); z-index: 900;
    background: $ink; color: #fff; border-radius: 10px; padding: 10px 14px; display: flex; gap: 8px; align-items: center;
    box-shadow: 0 6px 24px rgba(0,0,0,.25); flex-wrap: wrap; max-width: 96vw;
    .ab-count { margin-right: 6px; font-size: 13px; }
    .ab-warn { color: #ffd180; a { color: #ffd180; text-decoration: underline; } }
    .btn-secondary { background: transparent; color: #fff; border-color: #90a4ae; }
    .btn-danger { background: transparent; color: #ff8a9a; border-color: #ff8a9a; }
    .btn-link { color: #cfd8dc; }
  }

  .modal-overlay, .drawer-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: rgba(0,0,0,.45); z-index: 1000; }
  .modal-overlay { display: flex; align-items: center; justify-content: center; }
  .modal-card { background: #fff; border-radius: 8px; width: 640px; max-width: 94vw; max-height: 90vh; display: flex; flex-direction: column;
    box-shadow: 0 8px 30px rgba(0,0,0,.2);
    &.wide { width: 1080px; }
  }
  .modal-head, .drawer-head { display: flex; justify-content: space-between; align-items: center; padding: 14px 18px; border-bottom: 1px solid #eee;
    h4 { margin: 0; }
  }
  .modal-x { border: none; background: none; font-size: 22px; cursor: pointer; color: #888; }
  .modal-body { padding: 16px 18px; overflow-y: auto; font-size: 13px; color: #444;
    .form-row { margin: 12px 0;
      label { display: block; font-size: 12px; color: #777; margin-bottom: 3px; }
      input { border: 1px solid #ccc; border-radius: 4px; padding: 6px 10px; width: 100%; max-width: 420px; }
    }
    .hint { font-size: 12px; color: #888; }
    .howto { padding-left: 18px; li { margin: 4px 0; } }
  }
  .modal-foot { display: flex; justify-content: flex-end; gap: 8px; padding: 12px 18px; border-top: 1px solid #eee; flex-wrap: wrap; }
  .sim-summary, .confirm-summary { display: flex; gap: 10px; margin-bottom: 10px; flex-wrap: wrap;
    > div { flex: 1; min-width: 140px; background: #f7f9fb; border-radius: 6px; padding: 8px 12px; font-size: 12px; color: #666;
      span { display: block; font-size: 20px; font-weight: 700; color: $ink; }
    }
    .ok span { color: $green; } .warn span { color: $amber; } .err span { color: $red; }
  }
  .sim-groups { margin-bottom: 10px; font-size: 12px; }
  .unmatched { font-size: 12px; color: #c62828; margin-bottom: 8px; word-break: break-all; }
  .paste-box { width: 100%; box-sizing: border-box; border: 1px solid #ccc; border-radius: 4px; padding: 8px; font-family: monospace; font-size: 13px; resize: vertical; }
  .result-table { width: 100%; border-collapse: collapse; font-size: 12px;
    th, td { padding: 6px 8px; border-bottom: 1px solid #eee; text-align: left; vertical-align: top; }
    th { background: #fafafa; color: #555; position: sticky; top: 0; }
    .num { text-align: right; }
    .st-ok td { background: #f0fbf3; } .st-warn td { background: #fffaf0; } .st-err td { background: #fdf3f3; }
  }

  .drawer-overlay { display: flex; justify-content: flex-end; }
  .drawer { background: #fff; width: 560px; max-width: 100vw; height: 100%; display: flex; flex-direction: column; box-shadow: -6px 0 24px rgba(0,0,0,.2);
    .drawer-head h4 { margin: 0 0 4px; }
    .drawer-body { padding: 14px 18px; overflow-y: auto; font-size: 13px; }
    .kv { display: grid; grid-template-columns: 150px 1fr; gap: 4px 10px; margin: 0 0 12px;
      dt { color: #888; font-weight: 500; } dd { margin: 0; word-break: break-all; }
    }
    .drawer-actions { display: flex; gap: 8px; flex-wrap: wrap; margin-bottom: 14px; }
    h5 { margin: 6px 0; font-weight: 700; }
    .timeline { list-style: none; padding: 0; margin: 0;
      li { border-left: 3px solid #cfd8dc; padding: 4px 0 8px 10px; margin-left: 4px; }
      .t { color: #999; font-size: 11px; margin-right: 6px; }
      .who { font-size: 11px; color: #607d8b; }
      .ev-erro { border-left-color: $red; } .ev-migrada { border-left-color: $green; }
      .ev-retentativa { border-left-color: $amber; } .ev-revertida { border-left-color: #7e57c2; }
      .ev-vencimento_ajustado, .ev-mrr_ajustado { border-left-color: $blue; }
    }
  }
  .hist-box { background: #fff; border-radius: 6px; padding: 14px 16px; margin: 16px 0;
    .hist-head { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; margin-bottom: 8px;
      h4 { margin: 0; font-weight: 700; }
      .hist-actions { margin-left: auto; display: flex; gap: 8px; flex-wrap: wrap; }
    }
    .hist-chips { display: flex; gap: 6px; flex-wrap: wrap; margin-bottom: 10px;
      .chip { cursor: pointer; border: 1px solid #ccc; border-radius: 14px; padding: 3px 10px; font-size: 12px; user-select: none; }
      .chip.on { background: $blue; border-color: $blue; color: #fff; }
    }
    .hist-table { width: 100%; border-collapse: collapse; font-size: 13px;
      th { text-align: left; color: #888; font-weight: 500; border-bottom: 1px solid #eee; padding: 6px 8px; }
      td { border-bottom: 1px solid #f2f2f2; padding: 6px 8px; vertical-align: top; }
      td a { cursor: pointer; }
      .nowrap { white-space: nowrap; }
      tr.ev-erro td:nth-child(3) { color: $red; font-weight: 600; }
      tr.ev-migrada td:nth-child(3) { color: $green; font-weight: 600; }
      tr.ev-vencimento_ajustado td:nth-child(3), tr.ev-mrr_ajustado td:nth-child(3) { color: $blue; font-weight: 600; }
    }
    .hist-more { margin-top: 8px; text-align: center; }
  }
}
</style>



// WEBPACK FOOTER //
// src/vue/pages/restricted/payment/migracaoIuguAsaas/migracaoIuguAsaas.vue